/**
 * Cart Sidebar for Collection Pages
 *
 * Persistent sidebar showing cart contents, $40 minimum enforcement,
 * One-Time vs Subscribe toggle, Zapiet delivery options, and direct checkout.
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     Constants
     ----------------------------------------------------------------------- */
  /* Line-item images come straight from the Cart API, which returns the ORIGINAL upload —
     often 1000-1200px and 100-200 KB each. They are rendered into a 64x64 thumbnail (48x48
     below 400px), so almost every byte was wasted: Lighthouse attributed ~1.6 MB of the
     menu's image savings to these alone, the single largest item in the audit.

     Shopify's CDN resizes and converts on demand from query params, so ask it for a
     thumbnail-sized WebP instead. Non-Shopify URLs are passed through untouched. */
  var CART_THUMB_CSS_PX = 64;
  function cartThumbUrl(url, width) {
    if (!url) return '';
    if (url.indexOf('/cdn/shop/') === -1 && url.indexOf('cdn.shopify.com') === -1) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width + '&format=webp';
  }

  var GIFT_CARD_IDS = [31478736748586, 31478736781354, 31478736814122, 31478736846890];
  var ENABLE_ONETIME_TOGGLE_DISCOUNT = false;
  var ONETIME_TOGGLE_DISCOUNT_CODE = '20%20OFF';
  var ONETIME_TOGGLE_DISCOUNT_LABEL = '20% OFF';
  var ELIGIBLE_SUBSCRIPTION_CODE_STORAGE_KEY = 'cs_subscription_discount_code';

  var THRESHOLDS = {
    onetime:      { minimumOrder: 4000, freeDelivery: 6500 },
    subscription: { minimumOrder: 4000, freeDelivery: 6500 }
  };

  var USA_EAST_THRESHOLDS = {
    minimumOrder: 4000,
    freeDelivery: 7000,
    barMax: 10000
  };

  // Map collection path keywords → default Zapiet location IDs
  var COLLECTION_TO_ZAPIET = {
    'west':       137796,   // Calgary
    'east':       242677,   // Toronto
    'quebec':     319959,   // Montreal
    'usa':        278438,   // LA (USA West default)
    'usaeast':    301948,   // NY
    'default-us-menu': 278438,
    'default-ca-menu': 137796
  };

  var ZAPIET_SHOP = 'inspiredgo.myshopify.com';

  // Region → Zapiet location ID sets (mirrors addBundleProps logic; calgary is the fallback)
  var REGION_LOCATION_IDS = {
    'toronto':  [242677, 270213, 285794, 289774],
    'quebec':   [319959],
    'USA-West': [273216, 276649, 277960, 278438, 279269, 288292, 299051, 301750],
    'USA-East': [301948, 310459, 310466]
  };

  function safelyDecodeDiscountCode(code) {
    try {
      return decodeURIComponent(String(code).replace(/\+/g, '%20'));
    } catch (e) {
      return String(code);
    }
  }

  function normalizeDiscountCode(code) {
    var normalized = String(code || '').trim();
    var decoded;

    for (var i = 0; i < 2; i++) {
      decoded = safelyDecodeDiscountCode(normalized).trim();
      if (decoded === normalized) break;
      normalized = decoded;
    }

    normalized = normalized.toUpperCase();

    if (normalized === '20% OFF' || normalized === '20 OFF' || normalized === '20%20OFF') {
      return ONETIME_TOGGLE_DISCOUNT_CODE;
    }

    return normalized;
  }

  function getDiscountDisplayCode(code) {
    return normalizeDiscountCode(code) === ONETIME_TOGGLE_DISCOUNT_CODE
      ? ONETIME_TOGGLE_DISCOUNT_LABEL
      : normalizeDiscountCode(code);
  }

  // ReferralCandy friend codes (FRIEND-*): a flat amount off the first subscription
  // order, in cents, on carts at or above the subscription minimum — not a
  // percentage. One source for the amount; the labels below derive from it.
  var FRIEND_CODE_AMOUNT_OFF = 2000;
  var FRIEND_CODE_MIN_SUBTOTAL = 3800;
  var FRIEND_CODE_AMOUNT_LABEL = '$' + (FRIEND_CODE_AMOUNT_OFF / 100);

  // Codes that only apply to subscription purchases (mirrors getSubscriptionPromoConfig).
  function isSubscriptionOnlyCode(code) {
    var c = normalizeDiscountCode(code);
    if (!c) return false;
    return isWelcome30Code(c) || /25$/i.test(c) || c === 'GET50' || c.indexOf('FRIEND-') === 0 || c.indexOf('50OFF') !== -1 || c === 'MEALSFORMOMS';
  }

  // WELCOME30 earned-credit offer. All numbers and copy come from theme settings via
  // window.WELCOME30_OFFER (layout/theme.liquid); the Streak Credit Apps Script issues
  // the actual Recharge credit after each paid order — this is display only.
  function getWelcome30Offer() {
    var o = window.WELCOME30_OFFER;
    return o && o.code && o.cashback && o.cashback.length === 3 ? o : null;
  }
  function isWelcome30Code(code) {
    var o = getWelcome30Offer();
    return !!o && normalizeDiscountCode(code) === normalizeDiscountCode(o.code);
  }
  function fillTemplate(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, k) {
      return vars[k] !== undefined ? vars[k] : m;
    });
  }

  /* -----------------------------------------------------------------------
     CartSidebar Class
     ----------------------------------------------------------------------- */
  function CartSidebar() {
    this.cart = null;
    // Restore purchase type from localStorage, default to subscription
    var savedType = null;
    try { savedType = localStorage.getItem('cs_purchase_type'); } catch (e) {}
    this.purchaseType = (savedType === 'onetime') ? 'onetime' : 'subscription';
    this.frequency = 'weekly';
    this.deliveryDay = '';
    this.deliveryDate = '';
    this.onetimeDeliveryDate = '';
    this.subscriptionDeliveryDate = '';
    this.zapietLocationId = null;
    this.zapietCalendar = null;
    this.isCheckingOut = false;
    this._removeSubPromise = null;
    this.currentStep = 1; // 1 = cart items, 2 = delivery & order type

    // DOM references
    this.sidebar = document.getElementById('cartSidebar');
    if (!this.sidebar) return;

    this.content      = document.getElementById('csContent');
    this.loadingEl    = document.getElementById('csLoading');
    this.emptyEl      = document.getElementById('csEmpty');
    this.itemsEl      = document.getElementById('csItems');
    this.countEl      = document.getElementById('csItemCount');
    this.headerTotal  = document.getElementById('csHeaderTotal');
    this.progressText = document.getElementById('csProgressText');
    this.toggleEl     = document.getElementById('csPurchaseToggle');
    this.onetimePrice = document.getElementById('csOnetimePrice');
    this.subscribePrice = document.getElementById('csSubscribePrice');
    this.onetimeLabel = this.toggleEl ? this.toggleEl.querySelector('[data-type="onetime"] .cs-toggle-label') : null;
    this.subscribeLabel = this.toggleEl ? this.toggleEl.querySelector('[data-type="subscription"] .cs-toggle-label') : null;
    this.onetimeDelivery = document.getElementById('csOnetimeDelivery');
    this.subOptions      = document.getElementById('csSubscribeOptions');
    this.frequencySelect = document.getElementById('csFrequencySelect');
    this.daySelect       = document.getElementById('csDaySelect');
    this.subFirstOrder   = document.getElementById('csSubFirstOrder');
    this.footer       = document.getElementById('csFooter');
    this.checkoutBtn  = document.getElementById('csCheckoutBtn');
    this.desktopCount = document.getElementById('csDesktopCount');
    this.subtotalRow  = document.getElementById('csSubtotalRow');
    this.subtotalAmount = document.getElementById('csSubtotalAmount');
    this.deliveringRow = document.getElementById('csDeliveringRow');
    this.deliveringZip = document.getElementById('csDeliveringZip');
    this.mobileToggle   = document.getElementById('csMobileToggle');
    this.mobileOverlay  = document.getElementById('csMobileOverlay');
    this.mobileCount    = document.getElementById('csMobileCount');
    this.mobileSubtotal = document.getElementById('csMobileSubtotal');
    this.mobileAction   = document.getElementById('csMobileAction');
    this.mobileProgress     = document.getElementById('csMobileProgress');
    this.mobileProgressFill = document.getElementById('csMobileProgressFill');
    this.mobileProgressBar  = document.getElementById('csMobileProgressBar');
    this.mobileDotMin       = document.getElementById('csMobileDotMin');
    this.mobileDotFree      = document.getElementById('csMobileDotFree');
    this.mobileLabels       = document.getElementById('csMobileLabels');
    this.mobileLabelMin     = document.getElementById('csMobileLabelMin');
    this.mobileLabelFree    = document.getElementById('csMobileLabelFree');
    this.progressBarWrapper = document.getElementById('csProgressBarWrapper');
    this.progressBarText    = document.getElementById('csProgressBarText');
    this.progressFill       = document.getElementById('csProgressFill');

    // USA East milestone progress bar
    this.milestoneBar       = document.getElementById('csMilestoneBar');
    this.milestoneText      = document.getElementById('csMilestoneText');
    this.milestoneFill      = document.getElementById('csMilestoneFill');
    this.milestoneMinDot    = document.getElementById('csMilestoneMin');
    this.milestoneFreeDot   = document.getElementById('csMilestoneFree');
    this.milestoneLabelMin  = document.getElementById('csMilestoneLabelMin');
    this.milestoneLabelFree = document.getElementById('csMilestoneLabelFree');
    this.isUSAEast = window.location.pathname.toLowerCase().indexOf('/collections/usaeast') !== -1;
    this.isUS = window.location.hostname.endsWith('.com');

    this.onetimeCalendar    = document.getElementById('csOnetimeCalendar');
    this.calContainer       = document.getElementById('csCalContainer');
    this.calendarInitialized = false;
    this.enableDates = [];

    // Zip code input
    this.zipRow       = document.getElementById('csZipRow');
    this.zipInput     = document.getElementById('csZipInput');
    this.zipBtn       = document.getElementById('csZipBtn');
    this.zipMsg       = document.getElementById('csZipMsg');
    this.locationMismatchCta = document.getElementById('csLocationMismatchCta');
    this.selectLocationBtn   = document.getElementById('csSelectLocationBtn');
    this.switchConfirmOverlay  = document.getElementById('csSwitchConfirmOverlay');
    this.switchConfirmContinue = document.getElementById('csSwitchConfirmContinue');
    this.switchConfirmCancel   = document.getElementById('csSwitchConfirmCancel');
    this.deliveringChange = document.getElementById('csDeliveringChange');
    this.zipConfirmedCode = ''; // The currently confirmed postal code

    // Discount banner & savings
    this.discountBanner   = document.getElementById('csDiscountBanner');
    this.discountTitle    = document.getElementById('csDiscountTitle');
    this.discountTiers    = document.getElementById('csDiscountTiers');
    this.discountRow      = document.getElementById('csDiscountRow');
    this.discountLabel    = document.getElementById('csDiscountLabel');
    this.discountAmount   = document.getElementById('csDiscountAmount');
    this.subtotalOriginal = document.getElementById('csSubtotalOriginal');
    this.switchingOverlay = document.getElementById('csSwitchingOverlay');

    // WELCOME30 cash back boxes (one per step) + the (i) in the discount banner
    this.cashbackEls  = Array.prototype.slice.call(document.querySelectorAll('.cs-cashback'));
    this.discountInfo = document.getElementById('csDiscountInfo');
    var openOffer = function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof window.openWelcome30Popup === 'function') window.openWelcome30Popup();
    };
    this.cashbackEls.forEach(function (el) {
      var btn = el.querySelector('.cs-cashback-info');
      if (btn) btn.addEventListener('click', openOffer);
    });
    if (this.discountInfo) this.discountInfo.addEventListener('click', openOffer);

    // Waitlist form (non-serviceable zip)
    this.waitlist      = document.getElementById('csWaitlist');
    this.waitlistForm  = document.getElementById('csWaitlistForm');
    this.waitlistEmail = document.getElementById('csWaitlistEmail');
    this.waitlistMsg   = document.getElementById('csWaitlistMsg');

    // Step navigation
    this.stepsViewport = document.querySelector('.cs-steps-viewport');
    this.stepsTrack = document.getElementById('csStepsTrack');
    this.backLink   = document.getElementById('csBackToCart');

    this.itemTemplate = document.getElementById('csSidebarItemTemplate');

    this.init();
  }

  CartSidebar.prototype.init = function () {
    var self = this;

    // Restore toggle UI to match stored purchase type
    self.toggleEl.querySelectorAll('.cs-toggle-option').forEach(function (el) {
      el.classList.toggle('cs-toggle-active', el.dataset.type === self.purchaseType);
    });
    var activeRadio = self.toggleEl.querySelector('input[value="' + self.purchaseType + '"]');
    if (activeRadio) activeRadio.checked = true;
    if (self.purchaseType === 'subscription') {
      if (self.subOptions) self.subOptions.style.display = '';
      if (self.onetimeDelivery) self.onetimeDelivery.style.display = 'none';
      if (self.onetimeCalendar) self.onetimeCalendar.style.display = 'none';
    } else {
      if (self.subOptions) self.subOptions.style.display = 'none';
      if (self.onetimeDelivery) self.onetimeDelivery.style.display = '';
      if (self.onetimeCalendar) {
        if (self.calendarInitialized) {
          self.onetimeCalendar.style.display = '';
        } else {
          self.onetimeCalendar.style.display = 'none';
          self._autoShowCalendar = true;
        }
      }
    }

    // Adjust sidebar sticky position based on sticky header visibility
    this.initStickyHeaderSync();

    // Fetch initial cart then render
    this.fetchCart(function (cart) {
      self.cart = cart;

      // Restore zip from sessionStorage (survives page refresh)
      var ssZip = null, ssLoc = null;
      try {
        ssZip = sessionStorage.getItem('cs_zip');
        ssLoc = sessionStorage.getItem('cs_location_id');
      } catch (e) {}

      // Determine expected location from the current collection URL
      var urlLocationId = self.getZapietLocationFromUrl();
      var urlRegion = self.getRegionFromLocationId(urlLocationId);

      if (ssZip && ssLoc) {
        var storedLoc = parseInt(ssLoc, 10);
        var storedRegion = self.getRegionFromLocationId(storedLoc);

        if (storedRegion === urlRegion) {
          // Session matches current collection page — restore normally
          self.zapietLocationId = storedLoc;
          self.zipConfirmedCode = ssZip;
          self.fetchDeliveryOptions(ssZip);
        } else {
          // Stale session from a different region — clear it and use URL default
          console.warn('CartSidebar: sessionStorage region (' + storedRegion + ') does not match current page (' + urlRegion + '), resetting');
          self.zapietLocationId = urlLocationId;
          self.zipConfirmedCode = '';
          try {
            sessionStorage.removeItem('cs_zip');
            sessionStorage.removeItem('cs_location_id');
          } catch (e) {}
          try { window.dispatchEvent(new Event('inspiredgo:zip-updated')); } catch (e) {}
        }
      } else {
        // No session — use collection URL as default location
        self.zapietLocationId = urlLocationId;
      }

      // Ensure cart items match the stored purchase type
      var hasItems = cart.items && cart.items.length > 0;
      var hasSP = hasItems && cart.items.some(function (item) {
        return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      });

      var needsRebuild = false;
      if (self.purchaseType === 'subscription' && hasItems && !hasSP) {
        needsRebuild = true; // Items are one-time but subscription is selected
      } else if (self.purchaseType === 'onetime' && hasItems && hasSP) {
        needsRebuild = true; // Items have selling plans but one-time is selected
      }

      if (needsRebuild) {
        self.render(); // Render first so user sees the cart immediately
        self.rebuildCartForPurchaseType(self.purchaseType);
      } else {
        self.render();
      }
    });

    // Reset state when page is restored from bfcache (back button)
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        self.isCheckingOut = false;
        self.showCheckoutLoading(false);
        // Always return to Step 1 on bfcache restore
        self.goToStep(1);

        // Restore purchase type from localStorage
        var savedType = null;
        try { savedType = localStorage.getItem('cs_purchase_type'); } catch (ex) {}
        self.purchaseType = (savedType === 'onetime') ? 'onetime' : 'subscription';

        self.toggleEl.querySelectorAll('.cs-toggle-option').forEach(function (el) {
          el.classList.toggle('cs-toggle-active', el.dataset.type === self.purchaseType);
        });
        var activeRadio = self.toggleEl.querySelector('input[value="' + self.purchaseType + '"]');
        if (activeRadio) activeRadio.checked = true;

        if (self.purchaseType === 'subscription') {
          if (self.subOptions) self.subOptions.style.display = '';
          if (self.onetimeDelivery) self.onetimeDelivery.style.display = 'none';
          if (self.onetimeCalendar) self.onetimeCalendar.style.display = 'none';
        } else {
          if (self.subOptions) self.subOptions.style.display = 'none';
          if (self.onetimeDelivery) self.onetimeDelivery.style.display = '';
          if (self.onetimeCalendar) {
            if (self.calendarInitialized) {
              self.onetimeCalendar.style.display = '';
            } else {
              self.onetimeCalendar.style.display = 'none';
              self._autoShowCalendar = true;
            }
          }
        }

        // Ensure cart items match the purchase type
        self.fetchCart(function (cart) {
          self.cart = cart;
          var hasItems = cart.items && cart.items.length > 0;
          var hasSP = (cart.items || []).some(function (item) {
            return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
          });

          var needsRebuild = false;
          if (self.purchaseType === 'subscription' && hasItems && !hasSP) needsRebuild = true;
          else if (self.purchaseType === 'onetime' && hasItems && hasSP) needsRebuild = true;

          if (needsRebuild) {
            self.render();
            self.rebuildCartForPurchaseType(self.purchaseType);
          } else {
            self.render();
          }
        });
      }
    });

    // Listen for cart updates (skip if we just dispatched)
    document.addEventListener('cart:updated', function (e) {
      if (self._selfDispatch) { self._selfDispatch = false; return; }

      var onCartReady = function (cart) {
        self.cart = cart;
        self.render();
      };

      if (e.detail) {
        onCartReady(e.detail);
      } else {
        self.fetchCart(function (cart) { onCartReady(cart); });
      }
    });

    window.addEventListener('cartUpdatedZapiet', function () {
      self.fetchCart(function (cart) {
        self.cart = cart;
        self.render();
      });
    });

    // Bind item interactions (delegated)
    this.itemsEl.addEventListener('click', function (e) {
      var btn;
      if ((btn = e.target.closest('.cs-qty-minus'))) {
        self.handleQtyChange(btn, -1);
      } else if ((btn = e.target.closest('.cs-qty-plus'))) {
        self.handleQtyChange(btn, 1);
      } else if ((btn = e.target.closest('.cs-item-remove'))) {
        self.handleRemove(btn);
      }
    });

    this.itemsEl.addEventListener('change', function (e) {
      if (e.target.classList.contains('cs-qty-input')) {
        self.handleQtyDirect(e.target);
      }
    });

    // Purchase type toggle
    this.toggleEl.addEventListener('click', function (e) {
      var option = e.target.closest('.cs-toggle-option');
      if (!option) return;
      var type = option.dataset.type;
      if (type === self.purchaseType) return;

      self.purchaseType = type;
      try { localStorage.setItem('cs_purchase_type', type); } catch (e) {}
      document.dispatchEvent(new CustomEvent('cs:purchase-type-changed', {
        detail: { purchaseType: type }
      }));
      self.toggleEl.querySelectorAll('.cs-toggle-option').forEach(function (el) {
        el.classList.toggle('cs-toggle-active', el.dataset.type === type);
      });

      if (type === 'subscription') {
        self.subOptions.style.display = '';
        if (self.onetimeDelivery) self.onetimeDelivery.style.display = 'none';
        if (self.onetimeCalendar) self.onetimeCalendar.style.display = 'none';
        if (self.zapietLocationId && !self.zapietCalendar) {
          self.fetchDeliveryOptions();
        }
        self.updateDeliveryDate();
      } else {
        self.subOptions.style.display = 'none';
        if (self.onetimeDeliveryDate) self.deliveryDate = self.onetimeDeliveryDate;
        if (self.onetimeDelivery) self.onetimeDelivery.style.display = '';
        if (self.onetimeCalendar) {
          if (self.calendarInitialized) {
            self.onetimeCalendar.style.display = '';
          } else {
            // Calendar not yet initialized — show it as soon as init finishes
            self._autoShowCalendar = true;
          }
        }
      }

      // Rebuild cart with correct items for the selected purchase type
      self.rebuildCartForPurchaseType(type);
    });

    // Frequency select
    this.frequencySelect.addEventListener('change', function () {
      self.frequency = self.frequencySelect.value;
      self.autoSizeSelect(self.frequencySelect);
      self.updateDeliveryDate();
    });

    // Day select
    this.daySelect.addEventListener('change', function () {
      self.deliveryDay = self.daySelect.value;
      self.autoSizeSelect(self.daySelect);
      self.updateDeliveryDate();
    });

    // Checkout button — Step 1: go to Step 2, Step 2: actual checkout
    this.checkoutBtn.addEventListener('click', function () {
      if (self.locationMismatchCta) self.locationMismatchCta.style.display = 'none';
      // Always re-check minimum before any action
      var total = self.getEffectiveCheckoutTotal();
      var min = self.isUSAEast
        ? USA_EAST_THRESHOLDS.minimumOrder
        : ((self.purchaseType === 'subscription')
          ? THRESHOLDS.subscription.minimumOrder
          : THRESHOLDS.onetime.minimumOrder);
      if (!self.cart || total < min) {
        self.updateCheckoutButton();
        return;
      }

      if (self.currentStep === 1) {
        self.goToStep(2);
      } else if (!self.isCheckingOut) {
        if (self.zipConfirmedCode) {
          // Re-validate all cart items against the confirmed zip on every checkout attempt
          var prevZip = self.zipConfirmedCode;
          var prevRegion = self.getRegionFromLocationId(self.zapietLocationId);
          self.showCheckoutLoading(true);
          self.validateItemsForRegion(prevRegion).then(function (result) {
            self.showCheckoutLoading(false);
            if (!result.valid) {
              // Reset zip to input state with previous code pre-filled so user can fix
              self.resetZipCode();
              if (self.zipInput) self.zipInput.value = prevZip;
              self.updateCheckoutButton();
              if (self.zipMsg) self.zipMsg.textContent = 'We deliver to your area, but some items in your cart are not available for this location. Please choose items from the correct menu or enter a different postal code.';
              self.lastValidatedCountry = (prevRegion === 'USA-West' || prevRegion === 'USA-East') ? 'US' : 'CA';
              if (self.locationMismatchCta) self.locationMismatchCta.style.display = 'block';
              return;
            }
            self.checkout();
          }).catch(function () {
            // On network error fall through to checkout — don't block the user
            self.showCheckoutLoading(false);
            self.checkout();
          });
        } else {
          // Zip not yet confirmed — run full lookup + validate + checkout in one shot
          var enteredZip = self.zipInput ? (self.zipInput.value || '').trim() : '';
          if (!enteredZip) return;

          self.showCheckoutLoading(true);
          var lookupBody = new URLSearchParams();
          lookupBody.append('geoSearchQuery', enteredZip);
          lookupBody.append('shop', ZAPIET_SHOP);

          fetch('https://api.zapiet.com/v1.0/delivery/locations?shop=' + ZAPIET_SHOP, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded;' },
            body: lookupBody
          })
            .then(function (r) { return r.json(); })
            .then(function (res) {
              if (res.error || !res.id) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ event: 'non_serviceable_zip' });

                self.showCheckoutLoading(false);
                if (self.zipMsg) self.zipMsg.textContent = '';
                self.showWaitlist();
                self.updateCheckoutButton();
                return;
              }

              var resolvedRegion = self.getRegionFromLocationId(res.id);
              var resolvedLocationId = res.id;

              return self.validateItemsForRegion(resolvedRegion).then(function (result) {
                self.showCheckoutLoading(false);
                if (!result.valid) {
                  if (self.zipMsg) self.zipMsg.textContent = 'We deliver to your area, but some items in your cart are not available for this location. Please choose items from the correct menu or enter a different postal code.';
                  self.lastValidatedCountry = (resolvedRegion === 'USA-West' || resolvedRegion === 'USA-East') ? 'US' : 'CA';
                  if (self.locationMismatchCta) self.locationMismatchCta.style.display = 'block';
                  self.updateCheckoutButton();
                  return;
                }

                // All good — confirm zip and show options (same as Go button success)
                self.zapietLocationId = resolvedLocationId;
                self.zipConfirmedCode = enteredZip;
                try {
                  sessionStorage.setItem('cs_zip', enteredZip);
                  sessionStorage.setItem('cs_location_id', String(resolvedLocationId));
                } catch (e) {}
                try { window.dispatchEvent(new Event('inspiredgo:zip-updated')); } catch (e) {}
                self.showZipConfirmed(enteredZip);
                self.resetCalendar();
                self.saveZipToCart(resolvedLocationId);
                self.render();
                if (self.currentStep === 2) {
                  self.toggleEl.style.display = '';
                  if (self.purchaseType === 'subscription') {
                    self.subOptions.style.display = '';
                  }
                }
                self.fetchDeliveryOptions(enteredZip);
              });
            })
            .catch(function () {
              // On network error fall through to checkout — don't block the user
              self.showCheckoutLoading(false);
              self.checkout();
            });
        }
      }
    });

    // Zip code input
    if (this.zipBtn) {
      this.zipBtn.addEventListener('click', function () { self.lookupZipCode(); });
    }
    if (this.zipInput) {
      this.zipInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); self.lookupZipCode(); }
      });
      this.zipInput.addEventListener('input', function () { self.updateCheckoutButton(); });
    }
    if (this.selectLocationBtn) {
      this.selectLocationBtn.addEventListener('click', function () {
        self.showClearCartPopup();
      });
    }
    if (this.deliveringChange) {
      this.deliveringChange.addEventListener('click', function (e) {
        e.preventDefault();
        self.resetZipCode();
      });
    }
    // Mobile toggle
    if (this.mobileToggle) {
      this.mobileToggle.addEventListener('click', function () {
        self.toggleMobile();
      });
    }

    // Override header cart icon on collection pages — scroll to sidebar + glow
    var cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon) {
      cartIcon.removeAttribute('onclick');
      cartIcon.addEventListener('click', function (e) {
        e.preventDefault();
        if (window.matchMedia('(max-width: 767px)').matches) {
          self.toggleMobile();
        } else {
          var wrapper = self.sidebar.closest('.collection-sidebar-wrapper');
          if (wrapper) wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          self.sidebar.classList.add('cs-glow');
          setTimeout(function () { self.sidebar.classList.remove('cs-glow'); }, 1500);
        }
      });
    }

    // Continue shopping (scroll to grid)
    var continueLink = document.getElementById('csContinueShopping');
    if (continueLink) {
      continueLink.addEventListener('click', function (e) {
        e.preventDefault();
        var grid = document.querySelector('.collection-main');
        if (grid) grid.scrollIntoView({ behavior: 'smooth' });
      });
    }

    if (this.mobileOverlay) {
      this.mobileOverlay.addEventListener('click', function () {
        self.collapseMobile();
      });
    }

    // Back to cart link (Step 2 → Step 1)
    if (this.backLink) {
      this.backLink.addEventListener('click', function (e) {
        e.preventDefault();
        self.goToStep(1);
      });
    }

    // Waitlist form submission
    if (this.waitlistForm) {
      this.waitlistForm.addEventListener('submit', function (e) {
        e.preventDefault();
        self.submitWaitlist();
      });
    }
  };

  /* -----------------------------------------------------------------------
     Fetch Helpers
     ----------------------------------------------------------------------- */
  CartSidebar.prototype.fetchCart = function (cb) {
    fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(cb)
      .catch(function (err) { console.error('CartSidebar: fetch cart error', err); });
  };

  CartSidebar.prototype.getZapietLocationFromUrl = function () {
    var path = window.location.pathname.toLowerCase();
    var keys = Object.keys(COLLECTION_TO_ZAPIET);
    // Sort by length descending so longer matches win (e.g. 'usaeast' before 'usa')
    keys.sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < keys.length; i++) {
      if (path.indexOf('/collections/' + keys[i]) !== -1 || path.indexOf('/' + keys[i]) !== -1) {
        return COLLECTION_TO_ZAPIET[keys[i]];
      }
    }
    // Fallback: check if .com (US) or .ca (Canada)
    if (window.location.hostname.endsWith('.com')) return 278438; // LA
    return 137796; // Calgary
  };

  CartSidebar.prototype.getDeliveryZip = function () {
    // Sidebar's own confirmed code takes priority
    if (this.zipConfirmedCode) return this.zipConfirmedCode;
    if (!this.cart || !this.cart.attributes) return null;
    // cart-v2.js saves postal code as 'Postal-Input'
    return this.cart.attributes['Postal-Input'] ||
      this.cart.attributes['Delivery-Postal-Code'] ||
      null;
  };

  // Watch the sticky header and adjust sidebar top position accordingly
  CartSidebar.prototype.initStickyHeaderSync = function () {
    var sidebar = this.sidebar;
    var headerSection = document.getElementById('shopify-section-header');
    if (!sidebar || !headerSection) return;

    var GAP = 28; // gap between header bottom and sidebar top

    function updatePosition() {
      // Skip on mobile — sidebar uses fixed full-height layout
      if (window.innerWidth <= 749) {
        sidebar.style.top = '';
        sidebar.style.maxHeight = '';
        return;
      }

      var isSticky = headerSection.classList.contains('shopify-section-header-sticky');
      var isHidden = headerSection.classList.contains('shopify-section-header-hidden');

      if (!isSticky) {
        // Header at natural position (top of page) — default sidebar position
        sidebar.style.top = '20px';
        sidebar.style.maxHeight = 'calc(100vh - 40px)';
      } else if (isHidden) {
        // Header is sticky but scrolled away (hidden) — stick to top
        sidebar.style.top = GAP + 'px';
        sidebar.style.maxHeight = 'calc(100vh - ' + (GAP * 2) + 'px)';
      } else {
        // Header is sticky and visible — position below it
        var headerHeight = headerSection.offsetHeight;
        var top = headerHeight + GAP;
        sidebar.style.top = top + 'px';
        sidebar.style.maxHeight = 'calc(100vh - ' + (top + GAP) + 'px)';
      }
    }

    // Use MutationObserver to watch header class changes
    var observer = new MutationObserver(function () {
      updatePosition();
    });
    observer.observe(headerSection, { attributes: true, attributeFilter: ['class'] });

    // Initial position
    updatePosition();
  };

  /* -----------------------------------------------------------------------
     Rendering
     ----------------------------------------------------------------------- */
  CartSidebar.prototype.render = function () {
    if (!this.cart) return;

    var items = this.cart.items || [];
    var hasItems = items.length > 0;

    // Hide loader, toggle empty state
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    this.emptyEl.style.display = hasItems ? 'none' : '';
    this.itemsEl.style.display = hasItems ? '' : 'none';
    if (this.stepsViewport) this.stepsViewport.style.display = hasItems ? '' : 'none';

    // Header: "X added" and total (mobile)
    if (this.countEl) this.countEl.textContent = this.cart.item_count;
    if (this.headerTotal) this.headerTotal.textContent = '$' + (this.getCartTotal() / 100).toFixed(2);

    // Desktop header count
    if (this.desktopCount) {
      this.desktopCount.textContent = this.cart.item_count + ' item' + (this.cart.item_count !== 1 ? 's' : '');
    }

    // Desktop subtotal row
    if (this.subtotalRow) {
      this.subtotalRow.style.display = hasItems ? '' : 'none';
    }

    // Always update progress (it's in the header now)
    this.updateProgress();

    if (hasItems) {
      this.renderItems(items);
      this.renderDiscountBanner();
      this.updateTotals();
    } else {
      this.toggleEl.style.display = 'none';
      this.subOptions.style.display = 'none';
      if (this.discountBanner) this.discountBanner.style.display = 'none';
      if (this.discountRow) this.discountRow.style.display = 'none';
      // The offer block stays visible on an empty cart (no dollar estimate) so the
      // cash back is discoverable before the first item is added.
      this.renderCashback();
      // Go back to Step 1 if cart is emptied
      if (this.currentStep !== 1) this.goToStep(1);
    }

    // Update mobile toggle bar
    this.updateMobileBar();
  };

  CartSidebar.prototype.renderItems = function (items) {
    if (!this.itemTemplate) return;
    var frag = document.createDocumentFragment();
    var tpl = this.itemTemplate.content.querySelector('.cs-item');
    var promoConfig = this.getSubscriptionPromoConfig();
    var hasPromoSubscriptionDiscount = !!promoConfig && this.purchaseType === 'subscription';
    var self = this;

    items.forEach(function (item) {
      var el = tpl.cloneNode(true);

      el.dataset.lineKey = item.key;
      el.dataset.variantId = item.variant_id;

      var img = el.querySelector('.cs-item-img img');
      var rawImage = item.image || '';
      /* 128w covers 2x, 192w covers 3x screens; `sizes` is the fixed CSS box. */
      img.src = cartThumbUrl(rawImage, CART_THUMB_CSS_PX * 2);
      if (rawImage) {
        img.srcset = cartThumbUrl(rawImage, CART_THUMB_CSS_PX * 2) + ' ' + (CART_THUMB_CSS_PX * 2) + 'w, ' +
                     cartThumbUrl(rawImage, CART_THUMB_CSS_PX * 3) + ' ' + (CART_THUMB_CSS_PX * 3) + 'w';
        img.sizes = CART_THUMB_CSS_PX + 'px';
      }
      img.width = CART_THUMB_CSS_PX;
      img.height = CART_THUMB_CSS_PX;
      img.alt = item.product_title || '';

      var title = el.querySelector('.cs-item-title');
      title.textContent = item.product_title || '';

      var paired = el.querySelector('.cs-item-paired');
      if (item.properties && item.properties['Paired with']) {
        paired.textContent = 'Paired with ' + item.properties['Paired with'];
        paired.style.display = '';
      }

      var input = el.querySelector('.cs-qty-input');
      input.value = item.quantity;
      input.dataset.lineKey = item.key;

      var price = el.querySelector('.cs-item-price');
      var originalPrice = el.querySelector('.cs-item-price-original');
      var pctBadge = el.querySelector('.cs-item-price-pct');

      var itemProps = item.properties || {};
      if (String(itemProps._promo_gift) === 'true') {
        price.textContent = '$0.00';
        if (originalPrice && item.original_line_price > 0) {
          originalPrice.textContent = '$' + (item.original_line_price / 100).toFixed(2);
          originalPrice.style.display = '';
        }
        if (pctBadge) {
          pctBadge.textContent = '- 100%';
          pctBadge.style.display = '';
        }
      } else if (hasPromoSubscriptionDiscount && GIFT_CARD_IDS.indexOf(item.id) === -1) {
        var onetimeLine = self.getOnetimeLinePrice(item);
        var promoRate = promoConfig ? promoConfig.rate : 0;
        var subscriptionLine;
        if (self.purchaseType === 'subscription') {
          if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
            subscriptionLine = item.final_line_price;
          } else {
            var unitPrice = onetimeLine / item.quantity;
            subscriptionLine = Math.round(unitPrice * 0.95) * item.quantity;
          }
        } else {
          subscriptionLine = onetimeLine;
        }
        var fullyDiscountedLine = Math.round(subscriptionLine * (1 - promoRate));
        price.textContent = '$' + (fullyDiscountedLine / 100).toFixed(2);
        if (originalPrice && fullyDiscountedLine < onetimeLine) {
          originalPrice.textContent = '$' + (onetimeLine / 100).toFixed(2);
          originalPrice.style.display = '';
        }
        if (pctBadge && onetimeLine > 0) {
          var totalDiscountPct = Math.floor((1 - (1 - 0.05) * (1 - promoRate)) * 100);
          pctBadge.textContent = '- ' + totalDiscountPct + '%';
          pctBadge.style.display = '';
        }
      } else {
        price.textContent = '$' + (item.final_line_price / 100).toFixed(2);
        // Show strikethrough original price if item has a Shopify discount
        if (originalPrice && item.original_line_price !== item.final_line_price) {
          originalPrice.textContent = '$' + (item.original_line_price / 100).toFixed(2);
          originalPrice.style.display = '';
        }
        if (pctBadge && item.original_line_price > item.final_line_price) {
          var lineDiscountPercent = Math.round(((item.original_line_price - item.final_line_price) / item.original_line_price) * 100);
          pctBadge.textContent = '- ' + lineDiscountPercent + '%';
          pctBadge.style.display = '';
        }
      }

      frag.appendChild(el);
    });

    this.itemsEl.innerHTML = '';
    this.itemsEl.appendChild(frag);
  };

  /* -----------------------------------------------------------------------
     Progress & Totals
     ----------------------------------------------------------------------- */
  CartSidebar.prototype.getCartTotal = function () {
    var total = 0;
    (this.cart.items || []).forEach(function (item) {
      if (GIFT_CARD_IDS.indexOf(item.id) !== -1) return;
      var props = item.properties || {};
      if (String(props._promo_gift) === 'true') return;
      total += (item.original_line_price !== item.final_line_price)
        ? item.final_line_price
        : item.original_line_price;
    });
    return total;
  };

  CartSidebar.prototype.getOriginalTotal = function () {
    var total = 0;
    (this.cart.items || []).forEach(function (item) {
      if (GIFT_CARD_IDS.indexOf(item.id) !== -1) return;
      var props = item.properties || {};
      if (String(props._promo_gift) === 'true') return;
      total += item.original_line_price;
    });
    return total;
  };

  CartSidebar.prototype.updateProgress = function () {
    var total = this.getEffectiveCheckoutTotal();
    var thresholds = (this.purchaseType === 'subscription')
      ? THRESHOLDS.subscription
      : THRESHOLDS.onetime;

    var min = thresholds.minimumOrder;
    var freeDelivery = thresholds.freeDelivery;

    // USA East overrides both thresholds to flat values
    if (this.isUSAEast) {
      min = USA_EAST_THRESHOLDS.minimumOrder;
      freeDelivery = USA_EAST_THRESHOLDS.freeDelivery;
    }

    // Milestone bar for regions with a free-delivery tier (Canada + USA East)
    // Simple bar for regions without one (USA West)
    var hasFreeDelivery = this.isUSAEast || !this.isUS;

    if (hasFreeDelivery && this.milestoneBar) {
      if (this.progressBarWrapper) this.progressBarWrapper.style.display = 'none';
      this.updateMilestoneProgress(total, min, freeDelivery);
      this.updateCheckoutButton();
      return;
    }

    // --- Simple desktop progress bar (USA West — no free delivery) ---
    if (this.milestoneBar) this.milestoneBar.style.display = 'none';

    var percentage = Math.min((total / min) * 100, 100);
    var progressComplete = false;
    var progressText = '';

    if (total < min) {
      var needed = Math.ceil((min - total) / 100);
      progressText = 'Add at least <span class="cs-amount">$' + needed + ' more</span> to reach the $' + (min / 100) + ' minimum';
    } else {
      percentage = 100;
      progressComplete = true;
      progressText = 'You reached the minimum to checkout!';
    }

    if (this.progressBarWrapper) {
      this.progressBarWrapper.style.display = '';
      this.progressFill.style.width = percentage + '%';
      this.progressBarText.innerHTML = progressText;
      this.progressBarWrapper.classList.toggle('cs-progress-complete', progressComplete);
    }

    // --- Mobile header progress text (legacy, may not exist) ---
    if (this.progressText) {
      if (total < min) {
        var neededForBtn = Math.ceil((min - total) / 100);
        this.progressText.innerHTML = "You're <span class=\"cs-amount\">$" + neededForBtn + "</span> away from checkout";
        this.progressText.classList.remove('cs-progress-met');
      } else {
        this.progressText.textContent = "You're all set to checkout!";
        this.progressText.classList.add('cs-progress-met');
      }
    }

    this.updateCheckoutButton();
  };

  CartSidebar.prototype.updateMilestoneProgress = function (total, min, free) {
    var barMax = 10000;

    var minMet = total >= min;
    var freeMet = total >= free;

    var minPct = (min / barMax) * 100;
    var freePct = (free / barMax) * 100;
    var fillPct = Math.min((total / barMax) * 100, 100);

    this.milestoneBar.style.display = '';

    // Fill bar — amber → sage → forest green
    this.milestoneFill.style.width = fillPct + '%';
    this.milestoneFill.className = 'cs-milestone-fill' +
      (!minMet ? ' cs-milestone-fill--warning' : (!freeMet ? ' cs-milestone-fill--progress' : ''));

    // Min milestone dot
    this.milestoneMinDot.style.left = minPct + '%';
    this.milestoneMinDot.className = 'cs-milestone-dot' +
      (minMet ? ' cs-milestone-dot--met' : ' cs-milestone-dot--unmet');

    // Free delivery milestone dot
    this.milestoneFreeDot.style.left = freePct + '%';
    this.milestoneFreeDot.className = 'cs-milestone-dot' +
      (freeMet ? ' cs-milestone-dot--met' : ' cs-milestone-dot--unmet-muted');

    // Labels
    this.milestoneLabelMin.style.left = minPct + '%';
    this.milestoneLabelMin.textContent = '$' + (min / 100) + ' min';
    this.milestoneLabelMin.className = 'cs-milestone-label' +
      (minMet ? ' cs-milestone-label--met' : ' cs-milestone-label--unmet');

    this.milestoneLabelFree.style.left = freePct + '%';
    this.milestoneLabelFree.textContent = 'Free Delivery';
    this.milestoneLabelFree.className = 'cs-milestone-label' +
      (freeMet ? ' cs-milestone-label--met' : ' cs-milestone-label--muted');

    // Text above bar
    if (!minMet) {
      var needed = Math.ceil((min - total) / 100);
      this.milestoneText.innerHTML = '<span class="cs-milestone-icon">&#9888;</span> Add <strong>$' + needed + ' more</strong> to reach the $' + (min / 100) + ' minimum';
      this.milestoneText.className = 'cs-milestone-text cs-milestone-text--warning';
    } else if (!freeMet) {
      var awayFromFree = ((free - total) / 100).toFixed(2);
      this.milestoneText.innerHTML = 'You\'re <strong>$' + awayFromFree + '</strong> away from free delivery';
      this.milestoneText.className = 'cs-milestone-text cs-milestone-text--progress';
    } else {
      this.milestoneText.innerHTML = '<span class="cs-milestone-icon">&#10003;</span> You unlocked free delivery!';
      this.milestoneText.className = 'cs-milestone-text cs-milestone-text--success';
    }

    // Mobile header progress text
    if (this.progressText) {
      if (!minMet) {
        var neededMobile = Math.ceil((min - total) / 100);
        this.progressText.innerHTML = "You're <span class=\"cs-amount\">$" + neededMobile + "</span> away from checkout";
        this.progressText.classList.remove('cs-progress-met');
      } else if (!freeMet) {
        var awayMobile = ((free - total) / 100).toFixed(2);
        this.progressText.innerHTML = "$" + awayMobile + " away from <span class=\"cs-amount\">free delivery</span>";
        this.progressText.classList.add('cs-progress-met');
      } else {
        this.progressText.innerHTML = "&#10003; Free delivery unlocked!";
        this.progressText.classList.add('cs-progress-met');
      }
    }
  };

  // One-time price for the Subscribe/One-Time toggle display.
  // Sums one-time line prices (compare_at_price * qty for selling-plan items,
  // original_line_price otherwise) for all NON-gift, non-gift-card lines.
  // The auto-added free gift is skipped because its cart-level discount
  // already zeros it out — counting it would double-charge.
  CartSidebar.prototype.getOnetimePriceForDisplay = function () {
    var self = this;
    var total = 0;
    (this.cart.items || []).forEach(function (item) {
      if (GIFT_CARD_IDS.indexOf(item.id) !== -1) return;
      var props = item.properties || {};
      if (String(props._promo_gift) === 'true') return;
      total += self.getOnetimeLinePrice(item);
    });
    return total;
  };

  // Subscribe price for the Subscribe/One-Time toggle display.
  // For each NON-gift, non-gift-card line: use final_line_price if a selling
  // plan is already applied (Shopify has rounded per-unit already), else
  // synthesize 5%-off-per-unit. Gift line skipped (contributes $0).
  CartSidebar.prototype.getSubscribePriceForDisplay = function () {
    var total = 0;
    (this.cart.items || []).forEach(function (item) {
      if (GIFT_CARD_IDS.indexOf(item.id) !== -1) return;
      var props = item.properties || {};
      if (String(props._promo_gift) === 'true') return;
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        total += item.final_line_price;
      } else {
        var unitPrice = item.original_line_price / item.quantity;
        var discountedUnit = Math.round(unitPrice * 0.95);
        total += discountedUnit * item.quantity;
      }
    });
    return total;
  };

  // Calculate 5% subscription discount per-item (matches Shopify selling plan rounding)
  CartSidebar.prototype.getSubscriptionTotal = function () {
    var total = 0;
    (this.cart.items || []).forEach(function (item) {
      if (GIFT_CARD_IDS.indexOf(item.id) === -1) {
        // If item already has a selling plan, Shopify already applied the 5% discount
        if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
          total += item.final_line_price;
        } else {
          var unitPrice = item.original_line_price / item.quantity;
          var discountedUnit = Math.round(unitPrice * 0.95);
          total += discountedUnit * item.quantity;
        }
      }
    });
    return total;
  };

  CartSidebar.prototype.getEffectiveCheckoutTotal = function () {
    // Use post-discount line totals (getCartTotal) in both modes so that
    // discounted lines — including the auto-added free gift at $0 — do not
    // inflate progress against minimum-order / free-delivery thresholds.
    return this.getCartTotal();
  };

  // Get the one-time original price for a line item (before selling plan discount)
  // When a selling plan is attached, original_line_price may already include the 5% subscription discount.
  // Use selling_plan_allocation.compare_at_price for the true one-time unit price.
  CartSidebar.prototype.getOnetimeLinePrice = function (item) {
    if (item.selling_plan_allocation && item.selling_plan_allocation.compare_at_price != null) {
      return item.selling_plan_allocation.compare_at_price * item.quantity;
    }
    return item.original_line_price;
  };

  // Get the total one-time original price for all items (before any selling plan / discount)
  CartSidebar.prototype.getOnetimeOriginalTotal = function () {
    var self = this;
    var total = 0;
    (this.cart.items || []).forEach(function (item) {
      if (GIFT_CARD_IDS.indexOf(item.id) !== -1) return;
      var props = item.properties || {};
      if (String(props._promo_gift) === 'true') return;
      total += self.getOnetimeLinePrice(item);
    });
    return total;
  };

  CartSidebar.prototype.getDiscountPercent = function () {
    var orig = this.getOriginalTotal();
    var totalDiscount = (this.cart && this.cart.total_discount) ? this.cart.total_discount : 0;
    if (!totalDiscount) {
      totalDiscount = orig - this.getCartTotal();
    }
    if (orig > 0 && totalDiscount > 0) return Math.round((totalDiscount / orig) * 100);
    return 0;
  };

  CartSidebar.prototype.getCartDiscountCodes = function () {
    if (!this.cart || !this.cart.discount_codes) return [];
    return this.cart.discount_codes
      .map(function (dc) { return dc && dc.code ? normalizeDiscountCode(dc.code) : ''; })
      .filter(Boolean);
  };

  CartSidebar.prototype.getUrlDiscountCode = function () {
    try {
      var params = new URLSearchParams(window.location.search || '');
      var code = normalizeDiscountCode(params.get('discount') || '');
      return code || '';
    } catch (e) {
      return '';
    }
  };

  CartSidebar.prototype.getActiveDiscountCode = function () {
    var urlCode = this.getUrlDiscountCode();
    if (urlCode) return urlCode;

    var cookieCode = this.getCookie('discount_code');
    if (cookieCode) return normalizeDiscountCode(cookieCode);

    var cartCodes = this.getCartDiscountCodes();
    if (cartCodes.length > 0) return cartCodes[0];

    var apps = (this.cart && this.cart.cart_level_discount_applications) || [];
    if (apps.length > 0 && apps[0].title) return normalizeDiscountCode(apps[0].title);

    var itemDiscountTitle = '';
    (this.cart && this.cart.items || []).some(function (item) {
      var allocations = item.line_level_discount_allocations || [];
      if (!allocations.length) return false;
      var title = allocations[0].discount_application && allocations[0].discount_application.title;
      if (!title) return false;
      itemDiscountTitle = normalizeDiscountCode(title);
      return true;
    });
    if (itemDiscountTitle) return itemDiscountTitle;

    return '';
  };

  CartSidebar.prototype.getStoredEligibleSubscriptionCode = function () {
    try {
      return normalizeDiscountCode(localStorage.getItem(ELIGIBLE_SUBSCRIPTION_CODE_STORAGE_KEY) || '');
    } catch (e) {
      return '';
    }
  };

  CartSidebar.prototype.setStoredEligibleSubscriptionCode = function (code) {
    try {
      if (code) {
        localStorage.setItem(ELIGIBLE_SUBSCRIPTION_CODE_STORAGE_KEY, normalizeDiscountCode(code));
      } else {
        localStorage.removeItem(ELIGIBLE_SUBSCRIPTION_CODE_STORAGE_KEY);
      }
    } catch (e) {}
  };

  CartSidebar.prototype.getEligibleSubscriptionDiscountCode = function () {
    var candidates = [];
    var urlCode = this.getUrlDiscountCode();
    if (urlCode) candidates.push(urlCode);

    var cookieCode = this.getCookie('discount_code');
    if (cookieCode) candidates.push(normalizeDiscountCode(cookieCode));

    var cartCodes = this.getCartDiscountCodes();
    if (cartCodes.length) candidates = candidates.concat(cartCodes);

    var apps = (this.cart && this.cart.cart_level_discount_applications) || [];
    apps.forEach(function (app) {
      if (app && app.title) candidates.push(normalizeDiscountCode(app.title));
    });

    (this.cart && this.cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (alloc) {
        var title = alloc.discount_application && alloc.discount_application.title;
        if (title) candidates.push(normalizeDiscountCode(title));
      });
    });

    for (var i = 0; i < candidates.length; i++) {
      if (/25$/i.test(candidates[i])) {
        this.setStoredEligibleSubscriptionCode(candidates[i]);
        return candidates[i];
      }
    }

    var hasExplicitOtherCode = candidates.some(function (code) {
      return code && code !== ONETIME_TOGGLE_DISCOUNT_CODE;
    });

    if (hasExplicitOtherCode) {
      this.setStoredEligibleSubscriptionCode('');
      return '';
    }

    return this.getStoredEligibleSubscriptionCode();
  };

  CartSidebar.prototype.getToggleDiscountConfigs = function () {
    var subscriptionCode = this.getEligibleSubscriptionDiscountCode();
    if (!subscriptionCode || !/25$/i.test(subscriptionCode)) return null;

    return {
      subscription: {
        code: subscriptionCode,
        rate: 0.27,
        summaryLabel: '30% off first order',
        bannerTitle: 'Enjoy 30% off &mdash; ' + subscriptionCode + ' applied',
        bannerDetails: '30% off now &middot; 20% off 2nd &middot; 15% off 3rd &middot; 10% off 4th'
      },
      onetime: ENABLE_ONETIME_TOGGLE_DISCOUNT ? {
        code: ONETIME_TOGGLE_DISCOUNT_CODE,
        rate: 0.20,
        summaryLabel: '20% off one-time order',
        bannerTitle: 'Enjoy 20% off &mdash; ' + ONETIME_TOGGLE_DISCOUNT_LABEL + ' applied',
        bannerDetails: 'Switch to subscription to use ' + subscriptionCode + ' for 30% off your first subscription order'
      } : null
    };
  };

  CartSidebar.prototype.getSelectedPromoConfig = function () {
    if (this.purchaseType === 'subscription') return this.getSubscriptionPromoConfig();
    if (this.purchaseType === 'onetime') return this.getOnetimePromoConfig();
    return null;
  };

  CartSidebar.prototype.getSubscriptionPromoConfig = function () {
    var dualConfigs = this.getToggleDiscountConfigs();
    if (dualConfigs) {
      return dualConfigs.subscription;
    }

    var code = this.getActiveDiscountCode();
    if (!code) return null;

    var w30 = getWelcome30Offer();
    if (w30 && isWelcome30Code(code)) {
      var cb = w30.cashback;
      return {
        code: code,
        rate: (Number(w30.rate) || 27) / 100,
        summaryLabel: w30.firstPct + '% off first order',
        bannerTitle: fillTemplate(w30.copy.bannerTitle, { first: w30.firstPct, code: code }) || (w30.firstPct + '% off &mdash; ' + code + ' applied'),
        bannerDetails: w30.firstPct + '% off now &middot; ' + cb[0] + '% back + 5% off for 2nd &middot; ' +
          cb[1] + '% back + 5% off for 3rd &middot; ' + cb[2] + '% back + 5% off for 4th',
        welcome30: true
      };
    }

    if (/25$/i.test(code)) {
      return {
        code: code,
        rate: 0.27,
        summaryLabel: '30% off first order',
        bannerTitle: '30% off &mdash; ' + code + ' applied',
        bannerDetails: '30% off now &middot; 20% off 2nd &middot; 15% off 3rd &middot; 10% off 4th'
      };
    }

    // Flat-amount promo: amountOff replaces rate in the totals (getPromoDisplayTotals);
    // per-item prices just show the subscription price, since $20 off the order
    // has no per-line equivalent.
    if (code.indexOf('FRIEND-') === 0) {
      return {
        code: code,
        rate: 0,
        amountOff: FRIEND_CODE_AMOUNT_OFF,
        minSubtotal: FRIEND_CODE_MIN_SUBTOTAL,
        summaryLabel: FRIEND_CODE_AMOUNT_LABEL + ' off first order',
        bannerTitle: FRIEND_CODE_AMOUNT_LABEL + ' off &mdash; ' + code + ' applied',
        bannerDetails: FRIEND_CODE_AMOUNT_LABEL + ' off your first order &middot; 5% off future orders &middot; Subscription only'
      };
    }

    if (code === 'GET50' || code.indexOf('50OFF') !== -1) {
      return {
        code: code,
        rate: 0.50,
        summaryLabel: '50% off first order',
        bannerTitle: '50% off &mdash; ' + code + ' applied',
        bannerDetails: '50% off now &middot; 5% off future orders &middot; Subscription only'
      };
    }

    if (code === 'MEALSFORMOMS') {
      return {
        code: code,
        rate: 0.50,
        summaryLabel: '50% off',
        bannerTitle: '50% off &mdash; ' + code + ' applied',
        bannerDetails: '50% off now &middot; 5% off future orders &middot; Subscription only'
      };
    }

    return null;
  };

  CartSidebar.prototype.getOnetimePromoConfig = function () {
    var dualConfigs = this.getToggleDiscountConfigs();
    return dualConfigs && dualConfigs.onetime ? dualConfigs.onetime : null;
  };

  CartSidebar.prototype.getPromoDisplayTotals = function (promoConfig) {
    var promoRate = promoConfig ? promoConfig.rate : 0;
    var subtotal = 0;
    var savings = 0;
    var self = this;
    var isSubscription = this.purchaseType === 'subscription';

    (this.cart.items || []).forEach(function (item) {
      if (GIFT_CARD_IDS.indexOf(item.id) !== -1) return;
      var props = item.properties || {};
      if (String(props._promo_gift) === 'true') return;
      var onetimeLine = self.getOnetimeLinePrice(item);
      var baseLine;
      if (isSubscription) {
        if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
          baseLine = item.final_line_price;
        } else {
          var unitPrice = onetimeLine / item.quantity;
          baseLine = Math.round(unitPrice * 0.95) * item.quantity;
        }
      } else {
        baseLine = onetimeLine;
      }
      subtotal += baseLine;
    });

    if (promoConfig && promoConfig.amountOff) {
      // Flat amount off the order, only once the cart reaches the code's minimum.
      savings = subtotal >= (promoConfig.minSubtotal || 0) ? Math.min(promoConfig.amountOff, subtotal) : 0;
    } else {
      savings = Math.round(subtotal * promoRate);
    }

    return {
      subtotal: subtotal,
      savings: savings,
      total: Math.max(subtotal - savings, 0)
    };
  };

  CartSidebar.prototype.getSubscriptionPromoDisplayTotals = function () {
    return this.getPromoDisplayTotals(this.getSubscriptionPromoConfig());
  };

  CartSidebar.prototype.getOnetimePromoDisplayTotals = function () {
    return this.getPromoDisplayTotals(this.getOnetimePromoConfig());
  };

  CartSidebar.prototype.updateToggleLabels = function () {
    var dualConfigs = this.getToggleDiscountConfigs();
    var hasOnetimePromo = !!(dualConfigs && dualConfigs.onetime);

    if (this.subscribeLabel) {
      this.subscribeLabel.textContent = dualConfigs ? 'Subscribe & Save 30%' : 'Subscribe & Save 5%';
    }

    if (this.onetimeLabel) {
      this.onetimeLabel.textContent = hasOnetimePromo ? 'One-Time Purchase 20% Off' : 'One-Time Purchase';
    }
  };

  /**
   * Which cash back message applies right now, or null.
   *   - first order:   the WELCOME30 code is active → "earn cashback[0]% on this order"
   *   - returning:     customer tag WELCOME30_STAGE_N (written by the Apps Script after
   *                    order N) → order N+1 earns cashback[N] (N=1,2); N=3 is the final
   *                    order: its credit applies, nothing more is earned.
   *   - complete tag, one-time mode, or no offer → null
   * Dollar estimate = subscription subtotal (selling-plan prices, before the code) × pct,
   * the same pre-discount basis the Apps Script uses.
   */
  CartSidebar.prototype.getCashbackState = function () {
    var w30 = getWelcome30Offer();
    if (!w30 || !this.cart || this.purchaseType !== 'subscription') return null;

    var tags = (window.customer && window.customer.tags) || [];
    if (tags.indexOf(w30.completeTag) !== -1) return null;

    var stage = 0;
    tags.forEach(function (t) {
      if (String(t).indexOf(w30.stageTagPrefix) === 0) stage = Math.max(stage, parseInt(String(t).slice(w30.stageTagPrefix.length), 10) || 0);
    });

    var earnPct = 0, appliedPct = 0;
    if (stage >= 1 && stage <= 3) {
      appliedPct = w30.cashback[stage - 1];
      earnPct = stage < 3 ? w30.cashback[stage] : 0;
    } else if (isWelcome30Code(this.getActiveDiscountCode())) {
      earnPct = w30.cashback[0];
    } else {
      return null;
    }

    var basis = this.getSubscribePriceForDisplay();
    var amount = Math.round(basis * earnPct / 100);
    return { stage: stage, earnPct: earnPct, appliedPct: appliedPct, amount: amount, final: earnPct === 0 };
  };

  CartSidebar.prototype.renderCashback = function () {
    if (!this.cashbackEls || !this.cashbackEls.length) return;
    var state = this.getCashbackState();
    var w30 = getWelcome30Offer();
    if (!state || !w30) {
      this.cashbackEls.forEach(function (el) { el.style.display = 'none'; });
      return;
    }
    var vars = {
      pct: state.final ? state.appliedPct : state.earnPct,
      amount: '$' + (state.amount / 100).toFixed(2),
      first: w30.firstPct,
      code: w30.code
    };
    // Empty cart: keep the block (the offer is still active) but drop the dollar estimate.
    var subTemplate = state.final ? w30.copy.cartSubFinal
      : (state.amount > 0 ? w30.copy.cartSub : (w30.copy.cartSubEmpty || w30.copy.cartSubFinal));
    var title = fillTemplate(state.final ? w30.copy.cartTitleFinal : w30.copy.cartTitle, vars);
    var sub = fillTemplate(subTemplate, vars);
    this.cashbackEls.forEach(function (el) {
      el.querySelector('.cs-cashback-title').textContent = title;
      el.querySelector('.cs-cashback-sub').textContent = sub;
      el.style.display = '';
    });
  };

  CartSidebar.prototype.updateTotals = function () {
    this.renderCashback();
    var originalTotal = this.getOriginalTotal();
    var promoConfig = this.getSelectedPromoConfig();
    var hasSelectedPromoDiscount = !!promoConfig;
    var subscriptionPromoConfig = this.getSubscriptionPromoConfig();
    var onetimePromoConfig = this.getOnetimePromoConfig();

    this.updateToggleLabels();

    if (hasSelectedPromoDiscount) {
      var promoDisplay = this.getPromoDisplayTotals(promoConfig);
      var onetimeOrigTotal = this.getOnetimeOriginalTotal();
      var onetimeTotal = onetimePromoConfig ? this.getOnetimePromoDisplayTotals().total : this.getOnetimePriceForDisplay();
      var subscriptionTotal = subscriptionPromoConfig ? this.getSubscriptionPromoDisplayTotals().total : this.getSubscribePriceForDisplay();
      var displayTotal = promoDisplay.total / 100;

      if (this.headerTotal) this.headerTotal.textContent = '$' + displayTotal.toFixed(2);
      if (this.subtotalAmount) this.subtotalAmount.textContent = '$' + displayTotal.toFixed(2);

      if (this.subtotalOriginal) {
        this.subtotalOriginal.textContent = '$' + (onetimeOrigTotal / 100).toFixed(2);
        this.subtotalOriginal.style.display = '';
      }

      if (this.discountRow) {
        var totalSavings = onetimeOrigTotal - promoDisplay.total;
        if (totalSavings > 0) {
          if (promoConfig.amountOff) {
            // totalSavings also holds the 5% subscriber saving, so say so when it does.
            this.discountLabel.textContent = totalSavings > promoConfig.amountOff
              ? promoConfig.summaryLabel + ' + 5% subscriber savings'
              : promoConfig.summaryLabel;
          } else {
            var totalPct = Math.floor((1 - (1 - 0.05) * (1 - promoConfig.rate)) * 100);
            this.discountLabel.textContent = totalPct + '% off first order';
          }
          this.discountAmount.textContent = '- $' + (totalSavings / 100).toFixed(2);
          this.discountRow.style.display = '';
          if (this.subtotalRow) this.subtotalRow.classList.add('cs-has-discount');
        } else {
          this.discountRow.style.display = 'none';
          if (this.subtotalRow) this.subtotalRow.classList.remove('cs-has-discount');
        }
      }

      // Toggle prices
      if (this.onetimePrice) this.onetimePrice.textContent = '$' + (onetimeTotal / 100).toFixed(2);
      if (this.subscribePrice) this.subscribePrice.textContent = '$' + (subscriptionTotal / 100).toFixed(2);

      if (this.onetimeDelivery && this.deliveryDate) {
        var d = new Date(this.deliveryDate + 'T00:00:00');
        var shortDate = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        this.onetimeDelivery.innerHTML = 'Get your order on <b>' + shortDate + '</b>';
      }

      this.updateMobileBar();
      return;
    }

    var discountedTotal = this.getCartTotal();
    // Check item-level savings first, then fall back to cart-level total_discount
    var savings = originalTotal - discountedTotal;
    if (savings <= 0 && this.cart && this.cart.total_discount > 0) {
      savings = this.cart.total_discount;
      discountedTotal = originalTotal - savings;
    }
    var hasDiscount = savings > 0;
    var hasCartDiscount = hasDiscount;

    // In subscription mode, use one-time prices as the baseline so the 5%
    // subscription discount is visible (matches progress-bar calculation)
    if (this.purchaseType === 'subscription') {
      var onetimeOrigTotal = this.getOnetimeOriginalTotal();
      if (onetimeOrigTotal > discountedTotal) {
        savings = onetimeOrigTotal - discountedTotal;
        originalTotal = onetimeOrigTotal;
        hasDiscount = true;
      }
    }

    // Determine display subtotal based on purchase type
    var displayTotal = hasDiscount ? (discountedTotal / 100) : (originalTotal / 100);

    // Update header total (mobile)
    if (this.headerTotal) this.headerTotal.textContent = '$' + displayTotal.toFixed(2);

    // Desktop subtotal
    if (this.subtotalAmount) {
      this.subtotalAmount.textContent = '$' + displayTotal.toFixed(2);
    }

    // Show crossed-out original price when any discount exists (both purchase types)
    if (this.subtotalOriginal) {
      if (hasDiscount) {
        this.subtotalOriginal.textContent = '$' + (originalTotal / 100).toFixed(2);
        this.subtotalOriginal.style.display = '';
      } else {
        this.subtotalOriginal.style.display = 'none';
      }
    }

    // Discount savings row (both purchase types when discount exists)
    if (this.discountRow) {
      if (hasDiscount) {
        // Build label from Shopify discount data
        var apps = (this.cart && this.cart.cart_level_discount_applications) || [];
        var label = '';
        if (apps.length > 0) {
          label = apps[0].title;
        } else if (this.purchaseType === 'subscription' && !hasCartDiscount) {
          label = '5% subscription savings';
        } else {
          var pct = this.getDiscountPercent();
          label = pct + '% off';
        }
        this.discountLabel.textContent = label;
        this.discountAmount.textContent = '- $' + (savings / 100).toFixed(2);
        this.discountRow.style.display = '';
        if (this.subtotalRow) this.subtotalRow.classList.add('cs-has-discount');
      } else {
        this.discountRow.style.display = 'none';
        if (this.subtotalRow) this.subtotalRow.classList.remove('cs-has-discount');
      }
    }

    // Toggle prices:
    //   One-time = sum of one-time line prices for non-gift items.
    //   Subscribe = sum of subscription (5% off) line prices for non-gift items.
    // The auto-added free gift skips both helpers (its cart-level discount zeros it).
    if (this.onetimePrice) {
      var onetimeDisplayTotal = onetimePromoConfig ? this.getOnetimePromoDisplayTotals().total : this.getOnetimePriceForDisplay();
      this.onetimePrice.textContent = '$' + (onetimeDisplayTotal / 100).toFixed(2);
    }
    if (this.subscribePrice) {
      var subTotal;
      if (subscriptionPromoConfig) {
        subTotal = this.getSubscriptionPromoDisplayTotals().total;
      } else {
        subTotal = this.getSubscribePriceForDisplay();
      }
      this.subscribePrice.textContent = '$' + (subTotal / 100).toFixed(2);
    }

    // Update delivery info on one-time option
    if (this.onetimeDelivery && this.deliveryDate) {
      var d = new Date(this.deliveryDate + 'T00:00:00');
      var shortDate = d.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric'
      });
      this.onetimeDelivery.innerHTML = 'Get your order on <b>' + shortDate + '</b>';
    }

    // Update mobile bar
    this.updateMobileBar();
  };

  CartSidebar.prototype.renderDiscountBanner = function () {
    if (!this.discountBanner) return;

    this.updateToggleLabels();
    this.renderCashback();

    var promoConfig = this.getSelectedPromoConfig();

    if (promoConfig) {
      // getSelectedPromoConfig() already returns the config for the active
      // purchase type, so render it directly for whichever type is selected.
      this.discountTitle.innerHTML = promoConfig.bannerTitle;
      this.discountTiers.innerHTML = promoConfig.bannerDetails;
      if (this.discountInfo) this.discountInfo.style.display = promoConfig.welcome30 ? '' : 'none';
      this.discountTiers.style.display = '';
      this.discountBanner.style.display = '';
      this.initTiersScroll();
      return;
    }

    if (this.discountInfo) this.discountInfo.style.display = 'none';

    // When one-time is selected, subscription-only promo codes don't apply
    // (they're stripped at checkout), so exclude them from the banner.
    var isOnetime = this.purchaseType === 'onetime';

    var apps = (this.cart && this.cart.cart_level_discount_applications) || [];
    var displayApps = isOnetime
      ? apps.filter(function (app) { return !isSubscriptionOnlyCode(app.title); })
      : apps;

    // Tally item-level discounts (excluding subscription-only codes for one-time)
    var itemTitles = {};
    (this.cart && this.cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (alloc) {
        var rawTitle = alloc.discount_application ? alloc.discount_application.title : 'Discount';
        if (isOnetime && isSubscriptionOnlyCode(rawTitle)) return;
        if (!itemTitles[rawTitle]) itemTitles[rawTitle] = 0;
        itemTitles[rawTitle] += alloc.amount;
      });
    });
    var hasItemDiscount = Object.keys(itemTitles).length > 0;

    // Non-cart-level codes (URL/cookie/cart) — skip subscription-only for one-time
    var activeCode = this.getActiveDiscountCode();
    var hasDiscountCode = false;
    if (this.getUrlDiscountCode() || this.getCookie('discount_code')) {
      hasDiscountCode = true;
    } else if (this.cart && this.cart.discount_codes && this.cart.discount_codes.length > 0) {
      hasDiscountCode = true;
    }
    if (isOnetime && activeCode && isSubscriptionOnlyCode(activeCode)) {
      hasDiscountCode = false;
    }

    if (displayApps.length === 0 && !hasItemDiscount && !hasDiscountCode) {
      this.discountBanner.style.display = 'none';
      return;
    }

    // Build banner content from Shopify discount data
    var titleParts = [];
    var detailParts = [];

    displayApps.forEach(function (app) {
      var amount = '$' + (app.total_allocated_amount / 100).toFixed(2);
      titleParts.push(app.title);
      var typeLabel = app.type === 'discount_code' ? 'Discount Code'
        : app.type === 'automatic' ? 'Automatic Discount'
        : app.type === 'script' ? 'Script Discount' : 'Discount';
      detailParts.push(typeLabel + ' &mdash; ' + amount + ' off');
    });

    // Include item-level discount titles if no cart-level discounts
    if (displayApps.length === 0 && hasItemDiscount) {
      for (var title in itemTitles) {
        titleParts.push(title);
        detailParts.push('Item Discount &mdash; $' + (itemTitles[title] / 100).toFixed(2) + ' off');
      }
    }

    // Fallback: show discount code name from cart or cookie
    if (titleParts.length === 0 && hasDiscountCode) {
      if (activeCode) titleParts.push(getDiscountDisplayCode(activeCode));
    }

    if (titleParts.length === 0) {
      this.discountBanner.style.display = 'none';
      return;
    }

    this.discountTitle.innerHTML = titleParts.join(', ') + ' applied';
    this.discountTiers.innerHTML = detailParts.join('<br>');
    this.discountTiers.style.display = detailParts.length ? '' : 'none';

    this.discountBanner.style.display = '';
    this.initTiersScroll();
  };

  /* -----------------------------------------------------------------------
     Drag/swipe + auto-scroll helpers for overflowing banner text
     ----------------------------------------------------------------------- */
  CartSidebar.prototype._initDragScroll = function (el, stateKey) {
    if (!el) return;
    var state = this[stateKey];
    if (state) {
      el.removeEventListener('mousedown', state.mousedown);
      el.removeEventListener('touchstart', state.touchstart);
    }
    el.scrollLeft = 0;
    if (el.scrollWidth <= el.clientWidth) { this[stateKey] = null; return; }

    function onMouseDown(e) {
      el.classList.add('is-dragging');
      var startX = e.pageX, startScroll = el.scrollLeft;
      function onMove(e2) { el.scrollLeft = startScroll - (e2.pageX - startX); }
      function onUp() {
        el.classList.remove('is-dragging');
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      }
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    function onTouchStart(e) {
      var startX = e.touches[0].pageX, startScroll = el.scrollLeft;
      function onTouchMove(e2) { el.scrollLeft = startScroll - (e2.touches[0].pageX - startX); }
      function onTouchEnd() {
        el.removeEventListener('touchmove', onTouchMove);
        el.removeEventListener('touchend', onTouchEnd);
      }
      el.addEventListener('touchmove', onTouchMove, { passive: true });
      el.addEventListener('touchend', onTouchEnd);
    }
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    this[stateKey] = { mousedown: onMouseDown, touchstart: onTouchStart };
  };

  CartSidebar.prototype._initAutoScroll = function (el, stateKey) {
    if (!el) return;
    var state = this[stateKey];
    if (state) {
      clearInterval(state.timer);
      el.removeEventListener('mousedown', state.mousedown);
      el.removeEventListener('touchstart', state.touchstart);
    }
    if (el.scrollWidth <= el.clientWidth) { this[stateKey] = null; return; }

    var direction = 1, paused = false, resumeTimer = null;
    function pauseScroll() {
      paused = true;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () { paused = false; }, 3000);
    }
    var timer = setInterval(function () {
      if (paused) return;
      var max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      el.scrollLeft += direction * 0.6;
      if (el.scrollLeft >= max) { direction = -1; pauseScroll(); }
      if (el.scrollLeft <= 0) { direction = 1; pauseScroll(); }
    }, 20);

    function onMouseDown(e) {
      paused = true; clearTimeout(resumeTimer);
      el.classList.add('is-dragging');
      var startX = e.pageX, startScroll = el.scrollLeft;
      function onMove(e2) { el.scrollLeft = startScroll - (e2.pageX - startX); }
      function onUp() {
        el.classList.remove('is-dragging');
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        resumeTimer = setTimeout(function () { paused = false; }, 3000);
      }
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
    function onTouchStart(e) {
      paused = true; clearTimeout(resumeTimer);
      var startX = e.touches[0].pageX, startScroll = el.scrollLeft;
      function onTouchMove(e2) { el.scrollLeft = startScroll - (e2.touches[0].pageX - startX); }
      function onTouchEnd() {
        el.removeEventListener('touchmove', onTouchMove);
        el.removeEventListener('touchend', onTouchEnd);
        resumeTimer = setTimeout(function () { paused = false; }, 3000);
      }
      el.addEventListener('touchmove', onTouchMove, { passive: true });
      el.addEventListener('touchend', onTouchEnd);
    }
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    this[stateKey] = { timer: timer, mousedown: onMouseDown, touchstart: onTouchStart };
  };

  CartSidebar.prototype.initTiersScroll = function () {
    this._initDragScroll(this.discountTitle, '_titleScrollState');
    this._initAutoScroll(this.discountTiers, '_tiersScrollState');
  };

  /* -----------------------------------------------------------------------
     Quantity Handlers
     ----------------------------------------------------------------------- */
  CartSidebar.prototype.handleQtyChange = function (btn, delta) {
    if (this._updateInFlight) return;
    var item = btn.closest('.cs-item');
    var input = item.querySelector('.cs-qty-input');
    var newQty = Math.max(0, parseInt(input.value, 10) + delta);
    input.value = newQty;
    this.updateCartItem(item.dataset.lineKey, newQty);
  };

  CartSidebar.prototype.handleQtyDirect = function (input) {
    if (this._updateInFlight) return;
    var item = input.closest('.cs-item');
    var newQty = Math.max(0, parseInt(input.value, 10) || 0);
    input.value = newQty;
    this.updateCartItem(item.dataset.lineKey, newQty);
  };

  CartSidebar.prototype.handleRemove = function (btn) {
    if (this._updateInFlight) return;
    var item = btn.closest('.cs-item');
    this.updateCartItem(item.dataset.lineKey, 0);
  };

  CartSidebar.prototype.setQtyButtonsDisabled = function (disabled) {
    this.itemsEl.querySelectorAll('.cs-qty-btn, .cs-item-remove').forEach(function (el) {
      el.disabled = disabled;
      el.style.opacity = disabled ? '0.4' : '';
      el.style.pointerEvents = disabled ? 'none' : '';
    });
  };

  CartSidebar.prototype.updateCartItem = function (lineKey, qty) {
    var self = this;
    this._updateInFlight = true;
    this.setQtyButtonsDisabled(true);

    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) {
          return fetch('/cart.js', {
            method: 'GET',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' }
          }).then(function (r2) { return r2.json(); });
        }
        return r.json();
      })
      .then(function (cart) {
        if (cart && cart.items !== undefined) {
          self.cart = cart;
          self.render();
          self._selfDispatch = true;
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          if (typeof syncCollectionQuantities === 'function') syncCollectionQuantities(cart);
        }
      })
      .catch(function (err) { console.error('CartSidebar: update error', err); })
      .then(function () {
        self._updateInFlight = false;
        self.setQtyButtonsDisabled(false);
      });
  };

  /* -----------------------------------------------------------------------
     Auto-size select to fit selected option text
     ----------------------------------------------------------------------- */
  CartSidebar.prototype.autoSizeSelect = function (selectEl) {
    if (!selectEl) return;
    var tmp = document.createElement('span');
    tmp.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font-size:13px;font-weight:700;';
    tmp.textContent = selectEl.options[selectEl.selectedIndex].text;
    document.body.appendChild(tmp);
    selectEl.style.width = (tmp.offsetWidth + 18) + 'px';
    document.body.removeChild(tmp);
  };

  /* -----------------------------------------------------------------------
     Zip Code Lookup
     ----------------------------------------------------------------------- */

  /**
   * Look up the entered postal code via Zapiet locations API.
   * On success: saves location ID, fetches calendar, shows confirmed state.
   */
  CartSidebar.prototype.lookupZipCode = function () {
    var zip = (this.zipInput.value || '').trim();
    if (this.locationMismatchCta) this.locationMismatchCta.style.display = 'none';
    if (!zip) {
      this.zipMsg.textContent = 'Please enter a postal / zip code';
      return;
    }

    var self = this;
    this.zipMsg.textContent = '';
    this.zipBtn.disabled = true;
    this.zipBtn.textContent = '...';

    var body = new URLSearchParams();
    body.append('geoSearchQuery', zip);
    body.append('shop', ZAPIET_SHOP);

    fetch('https://api.zapiet.com/v1.0/delivery/locations?shop=' + ZAPIET_SHOP, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded;' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        self.zipBtn.disabled = false;
        self.zipBtn.textContent = 'Go';

        if (res.error || !res.id) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'non_serviceable_zip' });

          self.zipMsg.textContent = '';
          self.showWaitlist();
          self.updateCheckoutButton();
          return;
        }

        console.log('CartSidebar: zip lookup success', zip, '→ location', res.id);

        var resolvedRegion = self.getRegionFromLocationId(res.id);
        var locationId = res.id;

        // Validate cart items against resolved region before confirming
        self.zipBtn.disabled = true;
        self.zipBtn.textContent = 'Checking...';

        self.validateItemsForRegion(resolvedRegion).then(function (result) {
          self.zipBtn.disabled = false;
          self.zipBtn.textContent = 'Go';

          if (!result.valid) {
            self.zipMsg.textContent = 'We deliver to your area, but some items in your cart are not available for this location. Please choose items from the correct menu or enter a different postal code.';
            self.lastValidatedCountry = (resolvedRegion === 'USA-West' || resolvedRegion === 'USA-East') ? 'US' : 'CA';
            if (self.locationMismatchCta) self.locationMismatchCta.style.display = 'block';
            self.updateCheckoutButton();
            return;
          }

          // Notify progress bar that delivery is confirmed
          document.dispatchEvent(new CustomEvent('progressbar:stage-change', { detail: { stage: 3 } }));


          self.zapietLocationId = locationId;
          self.zipConfirmedCode = zip;

          // Persist to sessionStorage so it survives page refresh
          try {
            sessionStorage.setItem('cs_zip', zip);
            sessionStorage.setItem('cs_location_id', String(locationId));
          } catch (e) {}
          try { window.dispatchEvent(new Event('inspiredgo:zip-updated')); } catch (e) {}

          // If on a default-menu page, redirect to the correct region menu
          if (self.redirectToCorrectMenuIfDefault(resolvedRegion)) return;

          // Show confirmed state, hide input
          self.showZipConfirmed(zip);

          document.dispatchEvent(new CustomEvent('cs:zip-confirmed', { detail: { zip: zip, locationId: locationId } }));

          // Reset calendar so it re-inits with new location data
          self.resetCalendar();

          // Save location ID to cart attributes
          self.saveZipToCart(locationId);

          // Re-render and update button state
          self.render();

          // Show purchase toggle now that zip is confirmed (Step 2 context)
          if (self.currentStep === 2) {
            self.toggleEl.style.display = '';
            if (self.purchaseType === 'subscription') {
              self.subOptions.style.display = '';
            }
          }

          // Fetch delivery options (calendar + subscription slots)
          self.fetchDeliveryOptions(zip);
        }).catch(function (err) {
          console.error('CartSidebar: item validation error', err);
          self.zipBtn.disabled = false;
          self.zipBtn.textContent = 'Go';
          // On fetch error fall through and allow checkout (don't block on network failure)
          self.zapietLocationId = locationId;
          self.zipConfirmedCode = zip;
          try {
            sessionStorage.setItem('cs_zip', zip);
            sessionStorage.setItem('cs_location_id', String(locationId));
          } catch (e) {}
          try { window.dispatchEvent(new Event('inspiredgo:zip-updated')); } catch (e) {}
          if (self.redirectToCorrectMenuIfDefault(resolvedRegion)) return;
          self.showZipConfirmed(zip);
          self.resetCalendar();
          self.saveZipToCart(locationId);
          self.render();
          if (self.currentStep === 2) {
            self.toggleEl.style.display = '';
            if (self.purchaseType === 'subscription') {
              self.subOptions.style.display = '';
            }
          }
          self.fetchDeliveryOptions(zip);
        });
      })
      .catch(function (err) {
        console.error('CartSidebar: zip lookup error', err);
        self.zipBtn.disabled = false;
        self.zipBtn.textContent = 'Go';
        self.zipMsg.textContent = 'Something went wrong. Please try again.';
      });
  };

  /**
   * Returns the region name for a given Zapiet location ID.
   * Calgary is the fallback (matches addBundleProps 'default' behaviour).
   */
  CartSidebar.prototype.getRegionFromLocationId = function (locationId) {
    var regions = Object.keys(REGION_LOCATION_IDS);
    for (var i = 0; i < regions.length; i++) {
      if (REGION_LOCATION_IDS[regions[i]].indexOf(locationId) !== -1) {
        return regions[i];
      }
    }
    return 'calgary';
  };

  /**
   * If on a /collections/default-(ca|us)-menu page, redirect to the correct
   * region menu (cross-domain if needed). Returns true if redirect was initiated.
   */
  CartSidebar.prototype.redirectToCorrectMenuIfDefault = function (resolvedRegion) {
    var path = window.location.pathname;
    if (path !== '/collections/default-ca-menu' && path !== '/collections/default-us-menu') return false;
    var targetUrl;
    switch (resolvedRegion) {
      case 'toronto':  targetUrl = 'https://inspiredgo.ca/collections/east'; break;
      case 'quebec': targetUrl = 'https://inspiredgo.ca/collections/quebec'; break;
      case 'calgary':  targetUrl = 'https://inspiredgo.ca/collections/west'; break;
      case 'USA-East': targetUrl = 'https://inspiredgo.com/collections/usaeast'; break;
      case 'USA-West': targetUrl = 'https://inspiredgo.com/collections/usa'; break;
      default: return false;
    }

    // Cross-domain: pass zip via query param so the destination can re-validate
    // and save to sessionStorage on the new origin (sessionStorage doesn't cross domains).
    try {
      if (new URL(targetUrl).hostname !== window.location.hostname && this.zipConfirmedCode && this.zapietLocationId) {
        var u = new URL(targetUrl);
        u.searchParams.set('cs_zip', this.zipConfirmedCode);
        u.searchParams.set('cs_location_id', String(this.zapietLocationId));
        targetUrl = u.toString();
      }
    } catch (e) {}

    window.location.assign(targetUrl);
    return true;
  };

  /**
   * Fetches product tags for an array of unique product handles.
   * Returns a Promise resolving to { handle: tagsArray, ... }.
   */
  CartSidebar.prototype.fetchProductTags = function (handles) {
    var requests = handles.map(function (handle) {
      return fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (product) {
          return { handle: handle, tags: Array.isArray(product.tags) ? product.tags : [] };
        });
    });
    return Promise.all(requests).then(function (results) {
      var map = {};
      results.forEach(function (r) { map[r.handle] = r.tags; });
      return map;
    });
  };

  /**
   * Validates all cart items against the resolved delivery region.
   * Returns a Promise resolving to { valid: true } or { valid: false }.
   *
   * Availability rules (calgary/toronto/quebec + combination tags like Calgary_Toronto)
   * live in snippets/region-availability.liquid → window.IGRegion.
   */
  CartSidebar.prototype.validateItemsForRegion = function (resolvedRegion) {
    if (!this.cart || !this.cart.items || this.cart.items.length === 0) {
      return Promise.resolve({ valid: true });
    }

    // Collect unique handles (skip gift cards)
    var seenHandles = {};
    var handles = [];
    this.cart.items.forEach(function (item) {
      if (item.handle && !seenHandles[item.handle]) {
        seenHandles[item.handle] = true;
        handles.push(item.handle);
      }
    });

    return this.fetchProductTags(handles).then(function (tagMap) {
      for (var i = 0; i < handles.length; i++) {
        if (!window.IGRegion.isAvailableIn(tagMap[handles[i]] || [], resolvedRegion)) {
          return { valid: false };
        }
      }
      return { valid: true };
    });
  };

  // Map a collection URL → region name used by validateItemsForRegion
  CartSidebar.prototype.mapCollectionUrlToRegion = function (url) {
    try {
      var pathname = new URL(url, window.location.origin).pathname;
      if (pathname.indexOf('/collections/usaeast') !== -1) return 'USA-East';
      if (pathname.indexOf('/collections/usa') !== -1)     return 'USA-West';
      if (pathname.indexOf('/collections/quebec') !== -1)  return 'quebec';
      if (pathname.indexOf('/collections/east') !== -1)    return 'toronto';
      if (pathname.indexOf('/collections/west') !== -1)    return 'calgary';
    } catch (e) {}
    return null;
  };

  // Show the Switch Confirm modal; returns Promise<boolean> (true = continue)
  CartSidebar.prototype.showSwitchConfirmModal = function () {
    var self = this;
    return new Promise(function (resolve) {
      if (!self.switchConfirmOverlay) { resolve(true); return; }

      function cleanup() {
        if (self.switchConfirmContinue) self.switchConfirmContinue.removeEventListener('click', onContinue);
        if (self.switchConfirmCancel)   self.switchConfirmCancel.removeEventListener('click', onCancel);
        self.switchConfirmOverlay.removeEventListener('click', onBackdrop);
        self.switchConfirmOverlay.hidden = true;
      }
      function onContinue() { cleanup(); resolve(true); }
      function onCancel()   { cleanup(); resolve(false); }
      function onBackdrop(e) {
        if (e.target === self.switchConfirmOverlay) { cleanup(); resolve(false); }
      }

      if (self.switchConfirmContinue) self.switchConfirmContinue.addEventListener('click', onContinue);
      if (self.switchConfirmCancel)   self.switchConfirmCancel.addEventListener('click', onCancel);
      self.switchConfirmOverlay.addEventListener('click', onBackdrop);
      self.switchConfirmOverlay.hidden = false;
    });
  };

  // Decide what to do when user picks a region from the modal opened via cart sidebar
  // Returns Promise<'proceed' | 'close-only' | 'abort'>
  CartSidebar.prototype.handleRegionSwitch = function (collectionUrl) {
    var self = this;
    var targetUrl;
    try { targetUrl = new URL(collectionUrl, window.location.origin); }
    catch (e) { return Promise.resolve('proceed'); }

    // Same collection → no-op
    if (targetUrl.pathname === window.location.pathname && targetUrl.host === window.location.host) {
      return Promise.resolve('close-only');
    }

    // Cross-domain → don't clear (cart is per-domain)
    if (targetUrl.host !== window.location.host) {
      return Promise.resolve('proceed');
    }

    // Same domain, different collection — check for conflicts
    var targetRegion = this.mapCollectionUrlToRegion(collectionUrl);
    if (!targetRegion) return Promise.resolve('proceed');

    return this.validateItemsForRegion(targetRegion).then(function (result) {
      if (result.valid) return 'proceed';
      // Conflicts exist — ask user
      return self.showSwitchConfirmModal().then(function (confirmed) {
        if (!confirmed) return 'abort';
        return self.clearCartAndVerify().then(function () { return 'proceed'; })
          .catch(function (err) {
            console.error('CartSidebar: cart clear failed, aborting redirect:', err);
            return 'abort';
          });
      });
    });
  };

  CartSidebar.prototype.showZipConfirmed = function (zip) {
    // Hide the zip input row
    if (this.zipRow) this.zipRow.style.display = 'none';
    if (this.locationMismatchCta) this.locationMismatchCta.style.display = 'none';
    this.hideWaitlist();
    // Show the delivering row (the good-looking one with pin icon)
    if (this.deliveringRow) {
      this.deliveringZip.textContent = zip;
      this.deliveringRow.style.display = '';
    }
  };

  CartSidebar.prototype.resetZipCode = function () {
    this.zipConfirmedCode = '';
    try { sessionStorage.removeItem('cs_zip'); sessionStorage.removeItem('cs_location_id'); } catch (e) {}
    try { window.dispatchEvent(new Event('inspiredgo:zip-updated')); } catch (e) {}
    // Show the zip input row again
    if (this.zipRow) this.zipRow.style.display = '';
    if (this.locationMismatchCta) this.locationMismatchCta.style.display = 'none';
    if (this.zipInput) this.zipInput.value = '';
    if (this.zipMsg) this.zipMsg.textContent = '';
    // Hide the delivering row
    if (this.deliveringRow) this.deliveringRow.style.display = 'none';

    // Hide purchase toggle and calendar until new zip is set
    this.toggleEl.style.display = 'none';
    if (this.onetimeCalendar) this.onetimeCalendar.style.display = 'none';
    if (this.onetimeDelivery) this.onetimeDelivery.textContent = '';
    this.resetCalendar();

    // Update button state for current step
    this.updateCheckoutButton();
  };

  CartSidebar.prototype.resetCalendar = function () {
    // Destroy existing datepicker so it can be re-initialized with new data
    if (this.calendarInitialized && this.calContainer) {
      var jQ = (typeof jQuery !== 'undefined') ? jQuery : (typeof $ !== 'undefined') ? $ : null;
      if (jQ) {
        try { jQ(this.calContainer).datepicker('destroy'); } catch (e) {}
      }
      this.calContainer.innerHTML = '';
    }
    this.calendarInitialized = false;
    this.enableDates = [];
    this.zapietCalendar = null;
    this.deliveryDate = '';
    this.onetimeDeliveryDate = '';
    this.subscriptionDeliveryDate = '';
    this.deliveryDay = '';
    this._datepickerLoading = false;
  };

  CartSidebar.prototype.saveZipToCart = function (locationId) {
    var attrs = { 'Delivery-Location-Id': String(locationId) };
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function (err) { console.error('CartSidebar: save zip error', err); });
  };

  /* -----------------------------------------------------------------------
     Zapiet Delivery Options
     ----------------------------------------------------------------------- */
  CartSidebar.prototype.fetchDeliveryOptions = function (postalCode) {
    if (!this.zapietLocationId) return;
    var self = this;
    var url = 'https://api.zapiet.com/v1.0/delivery/locations/' + this.zapietLocationId +
      '/calendar?shop=' + ZAPIET_SHOP;

    postalCode = postalCode || this.zipConfirmedCode || '';
    var body = new URLSearchParams();
    body.append('shop', ZAPIET_SHOP);
    if (postalCode) {
      body.append('geoSearchQuery', postalCode);
    }

    var fetchOpts = {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded;' },
      body: body
    };

    fetch(url, fetchOpts)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data) return;
        console.log('CartSidebar: Zapiet calendar response', { disabled: data.disabled, minDate: data.minDate, slots: data.slots ? data.slots.length : 0 });
        self._calendarData = data;

        // Use slots array for subscription day select
        if (Array.isArray(data.slots) && data.slots.length > 0) {
          self.zapietCalendar = data.slots;
          self.buildDaySelect(data.slots);
        }

        // Initialize one-time calendar picker
        self.initOnetimeCalendar(data);

        // Set one-time delivery date from the first date Zapiet lets the calendar select.
        var firstSelectableDate = getFirstSelectableDate(data);
        if (firstSelectableDate) {
          var d = firstSelectableDate;
          var shortFormatted = d.toLocaleDateString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric'
          });
          self.onetimeDeliveryDate = getIsoFromDate(firstSelectableDate);
          if (self.purchaseType !== 'subscription') {
            self.deliveryDate = self.onetimeDeliveryDate;
          }
          if (self.onetimeDelivery) {
            self.onetimeDelivery.innerHTML = 'Get your order on <b>' + shortFormatted + '</b>';
          }
        }
      })
      .catch(function (err) { console.error('CartSidebar: Zapiet error', err); });
  };

  /**
   * Dynamically load Bootstrap Datepicker JS + CSS if not already available.
   * theme.liquid only includes it on the cart page template.
   */
  CartSidebar.prototype.loadDatepicker = function (callback) {
    var jQ = (typeof jQuery !== 'undefined') ? jQuery : (typeof $ !== 'undefined') ? $ : null;
    if (jQ && jQ.fn && jQ.fn.datepicker) { callback(); return; }
    if (this._datepickerLoading) return;
    this._datepickerLoading = true;

    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/bootstrap-datepicker/1.9.0/css/bootstrap-datepicker.min.css';
    document.head.appendChild(link);

    var script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/bootstrap-datepicker/1.9.0/js/bootstrap-datepicker.min.js';
    script.onload = function () { callback(); };
    script.onerror = function () { console.error('CartSidebar: failed to load bootstrap-datepicker'); };
    document.head.appendChild(script);
  };

  /**
   * Initialize Bootstrap Datepicker for one-time delivery date selection.
   * Uses the same disabled/enabled logic as cart-v2.js.
   */
  CartSidebar.prototype.initOnetimeCalendar = function (calendarData) {
    if (!this.calContainer) { console.warn('CartSidebar cal: no calContainer'); return; }
    if (!calendarData) { console.warn('CartSidebar cal: no calendarData'); return; }
    if (this.calendarInitialized) return;

    this._calendarData = calendarData;

    // Resolve jQuery
    var jQ = (typeof jQuery !== 'undefined') ? jQuery : (typeof $ !== 'undefined') ? $ : null;
    if (!jQ || !jQ.fn || !jQ.fn.datepicker) {
      // Dynamically load datepicker (theme.liquid only includes it on /cart)
      var self = this;
      this.loadDatepicker(function () {
        self.initOnetimeCalendar(calendarData);
      });
      return;
    }

    try {
      var self = this;
      var $cal = jQ(this.calContainer);
      var disabled = calendarData.disabled || [];
      var minDateTime = calendarData.minDateTime || calendarData.minDate || null;

      // Cap selectable range to 3 months from today
      var endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
      endDate.setMonth(endDate.getMonth() + 3);
      this.calendarEndDate = endDate;

      this.enableDates = [];
      var daysOfWeekDisabled = [];
      var datesDisabled = [];

      // Whitelist mode: disabled[0] === true means only listed dates are enabled
      if (disabled.length > 0 && disabled[0] === true) {
        disabled.slice(1).forEach(function (entry) {
          if (Array.isArray(entry)) {
            var y = entry[0];
            var m = (entry[1] + 1) < 10 ? '0' + (entry[1] + 1) : String(entry[1] + 1);
            var d = entry[2] < 10 ? '0' + entry[2] : String(entry[2]);
            var iso = y + '-' + m + '-' + d;
            // Drop any whitelisted date past the 3-month cap
            var entryDate = new Date(iso + 'T00:00:00');
            if (entryDate <= endDate) {
              self.enableDates.push(iso);
            }
          }
        });
      }
      // Blacklist mode: numeric = disabled day of week, array = disabled specific date
      else {
        disabled.forEach(function (entry) {
          if (Array.isArray(entry)) {
            datesDisabled.push(entry[0] + '-' + (entry[1] + 1) + '-' + entry[2]);
          } else {
            // Same mapping as cart-v2.js: day - 1
            daysOfWeekDisabled.push(entry - 1);
          }
        });
      }

      // Temporarily make the calendar container visible so datepicker renders with
      // correct dimensions (initializing on a hidden element causes zero-size layout)
      this.onetimeCalendar.style.visibility = 'hidden';
      this.onetimeCalendar.style.position = 'absolute';
      this.onetimeCalendar.style.display = 'block';

      // Initialize datepicker
      $cal.datepicker({
        format: 'yyyy-mm-dd',
        todayHighlight: true,
        endDate: endDate,
        beforeShowDay: function (date) {
          // Block anything past the 3-month cap (also blocks selection in whitelist mode)
          if (date > self.calendarEndDate) {
            return { enabled: false };
          }
          if (self.enableDates.length > 0) {
            var y = date.getFullYear();
            var m = String(date.getMonth() + 1).padStart(2, '0');
            var d = String(date.getDate()).padStart(2, '0');
            var iso = y + '-' + m + '-' + d;
            return { enabled: self.enableDates.indexOf(iso) !== -1 };
          }
          return { enabled: true };
        }
      });

      // Enforce 3-month cap (hides next-month arrow once reached)
      $cal.datepicker('setEndDate', endDate);

      // Apply blacklist constraints
      if (daysOfWeekDisabled.length > 0) {
        $cal.datepicker('setDaysOfWeekDisabled', daysOfWeekDisabled);
      }
      if (datesDisabled.length > 0) {
        $cal.datepicker('setDatesDisabled', datesDisabled);
      }

      // Set start date and initial selection
      if (minDateTime) {
        var startDate = new Date(String(minDateTime).replace(/\s+/g, 'T').concat('.000+00:00'));
        $cal.datepicker('setStartDate', startDate);
        if (self.enableDates.length > 0 && self.enableDates[0]) {
          $cal.datepicker('setDate', new Date(self.enableDates[0] + 'T00:00:00'));
        } else {
          $cal.datepicker('setDate', startDate);
        }
      }

      // Restore — hide for now, show logic below will decide final state
      this.onetimeCalendar.style.visibility = '';
      this.onetimeCalendar.style.position = '';
      this.onetimeCalendar.style.display = 'none';

      // Handle date selection
      $cal.datepicker().on('changeDate', function (e) {
        if (!e.date) return;
        var y = e.date.getFullYear();
        var m = String(e.date.getMonth() + 1).padStart(2, '0');
        var d = String(e.date.getDate()).padStart(2, '0');
        self.onetimeDeliveryDate = y + '-' + m + '-' + d;
        if (self.purchaseType === 'onetime') {
          self.deliveryDate = self.onetimeDeliveryDate;
        }

        // Set deliveryDay from the selected date (e.g. "sunday", "monday", "friday")
        var dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        self.deliveryDay = dayNames[e.date.getDay()];

        var formatted = e.date.toLocaleDateString('en-US', {
          weekday: 'short', month: 'short', day: 'numeric'
        });
        if (self.onetimeDelivery) {
          self.onetimeDelivery.innerHTML = 'Get your order on <b>' + formatted + '</b>';
        }
      });

      this.calendarInitialized = true;
      console.log('CartSidebar: calendar initialized OK, enableDates:', this.enableDates.length, 'daysOfWeekDisabled:', daysOfWeekDisabled);

      // Auto-show the calendar if one-time is the active purchase type, or if a
      // user toggle requested it before init completed.
      if (this.onetimeCalendar && (this.purchaseType === 'onetime' || this._autoShowCalendar)) {
        this.onetimeCalendar.style.display = '';
        this._autoShowCalendar = false;
      }
    } catch (err) {
      console.error('CartSidebar: calendar init error', err);
    }
  };

  // Valid selling-plan charge days (subscriptions only support these)
  var VALID_CHARGE_DAYS = ['sunday', 'monday'];
  var DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  function getIsoFromZapietEntry(entry) {
    if (!Array.isArray(entry)) return '';
    var year = entry[0];
    var month = String(entry[1] + 1).padStart(2, '0');
    var day = String(entry[2]).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  function getIsoFromDate(date) {
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  }

  function parseZapietDate(value) {
    if (!value) return null;
    var iso = String(value).slice(0, 10);
    var parts = iso.split('-');
    if (parts.length !== 3) return null;
    var year = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10) - 1;
    var day = parseInt(parts[2], 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    var parsed = new Date(year, month, day);
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }

  function getCalendarStartDate(calendarData) {
    if (!calendarData) return null;
    return parseZapietDate(calendarData.minDateTime || calendarData.minDate);
  }

  function isCalendarDateSelectable(calendarData, date) {
    if (!calendarData || !date) return true;

    var startDate = getCalendarStartDate(calendarData);
    if (startDate && date < startDate) return false;

    var disabled = calendarData.disabled || [];
    var iso = getIsoFromDate(date);

    // Whitelist mode: only the listed dates are selectable.
    if (disabled.length > 0 && disabled[0] === true) {
      for (var i = 1; i < disabled.length; i++) {
        if (getIsoFromZapietEntry(disabled[i]) === iso) return true;
      }
      return false;
    }

    for (var j = 0; j < disabled.length; j++) {
      var entry = disabled[j];
      if (Array.isArray(entry)) {
        if (getIsoFromZapietEntry(entry) === iso) return false;
      } else if (typeof entry === 'number' && date.getDay() === entry - 1) {
        return false;
      }
    }

    return true;
  }

  function getFirstSelectableDate(calendarData, selectedDay) {
    if (!calendarData) return null;

    var startDate = getCalendarStartDate(calendarData) || new Date();
    startDate.setHours(0, 0, 0, 0);

    var disabled = calendarData.disabled || [];
    if (disabled.length > 0 && disabled[0] === true) {
      var whitelistDates = [];
      for (var i = 1; i < disabled.length; i++) {
        var whitelistedDate = parseZapietDate(getIsoFromZapietEntry(disabled[i]));
        if (!whitelistedDate || whitelistedDate < startDate) continue;
        if (selectedDay && DAY_NAMES[whitelistedDate.getDay()] !== selectedDay) continue;
        whitelistDates.push(whitelistedDate);
      }
      whitelistDates.sort(function (a, b) { return a - b; });
      return whitelistDates[0] || null;
    }

    var cursor = new Date(startDate.getTime());
    var guard = 0;
    while (guard < 100) {
      if ((!selectedDay || DAY_NAMES[cursor.getDay()] === selectedDay) && isCalendarDateSelectable(calendarData, cursor)) {
        return cursor;
      }
      cursor.setDate(cursor.getDate() + 1);
      guard++;
    }

    return null;
  }

  CartSidebar.prototype.buildDaySelect = function (calendarData) {
    if (!Array.isArray(calendarData) || calendarData.length === 0) {
      this.daySelect.innerHTML = '<option value="">No dates available</option>';
      return;
    }

    // Extract unique days of week available in the calendar
    var availableDays = {};
    calendarData.forEach(function (entry) {
      if (entry.day_of_week) {
        availableDays[entry.day_of_week.toLowerCase()] = true;
      }
    });

    // For subscriptions, only show days that are valid charge days
    var allowedDays = VALID_CHARGE_DAYS.filter(function (day) {
      return availableDays[day];
    });

    if (allowedDays.length === 0) {
      this.daySelect.innerHTML = '<option value="">No subscription days available</option>';
      return;
    }

    var html = '';
    var firstVal = '';
    allowedDays.forEach(function (day) {
      var capitalized = day.charAt(0).toUpperCase() + day.slice(1);
      if (!firstVal) firstVal = day;
      html += '<option value="' + day + '">' + capitalized + '</option>';
    });

    this.daySelect.innerHTML = html;
    this.deliveryDay = firstVal;
    this.daySelect.value = firstVal;
    this.autoSizeSelect(this.daySelect);
    this.autoSizeSelect(this.frequencySelect);
    this.updateDeliveryDate();
  };

  CartSidebar.prototype.updateDeliveryDate = function () {
    if (!this.zapietCalendar || !this.deliveryDay) {
      if (this.subFirstOrder) this.subFirstOrder.textContent = '';
      return;
    }

    // Find next date matching the selected day
    var selectedDay = this.deliveryDay.toLowerCase();
    var calendarData = this._calendarData || {};
    var calendarStartDate = getCalendarStartDate(calendarData);
    var matchingSlots = [];
    for (var i = 0; i < this.zapietCalendar.length; i++) {
      if (String(this.zapietCalendar[i].day_of_week || '').toLowerCase() === selectedDay) {
        matchingSlots.push(this.zapietCalendar[i]);
      }
    }
    matchingSlots.sort(function (a, b) {
      var aDate = parseZapietDate(a.date);
      var bDate = parseZapietDate(b.date);
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate - bDate;
    });

    var match = null;
    if (matchingSlots.length) {
      if (this.purchaseType === 'subscription') {
        // Use the next subscription slot that is also selectable in Zapiet's calendar.
        var minSubscriptionDate = new Date();
        minSubscriptionDate.setHours(0, 0, 0, 0);
        if (calendarStartDate && calendarStartDate > minSubscriptionDate) {
          minSubscriptionDate = calendarStartDate;
        }

        for (var j = 0; j < matchingSlots.length; j++) {
          if (!matchingSlots[j].date) continue;
          var slotDate = parseZapietDate(matchingSlots[j].date);
          if (!slotDate) continue;
          if (slotDate >= minSubscriptionDate && isCalendarDateSelectable(calendarData, slotDate)) {
            match = matchingSlots[j];
            break;
          }
        }
      }

      if (!match && this.purchaseType !== 'subscription') {
        match = matchingSlots[0];
      }
    }

    if (match) {
      var d;
      if (match.date) {
        this.deliveryDate = match.date;
        d = parseZapietDate(match.date);
      } else {
        // Slots don't have date, calculate next selectable occurrence from Zapiet's start date.
        var targetIdx = DAY_NAMES.indexOf(selectedDay);
        d = calendarStartDate ? new Date(calendarStartDate.getTime()) : new Date();
        d.setHours(0, 0, 0, 0);
        var diff = (targetIdx - d.getDay() + 7) % 7;
        d.setDate(d.getDate() + diff);
        var guard = 0;
        while (!isCalendarDateSelectable(calendarData, d) && guard < 100) {
          d.setDate(d.getDate() + 7);
          guard++;
        }
        if (guard >= 100 && !isCalendarDateSelectable(calendarData, d)) {
          d = null;
        } else {
          this.deliveryDate = getIsoFromDate(d);
        }
      }
    } else if (this.purchaseType === 'subscription') {
      var fallbackIdx = DAY_NAMES.indexOf(selectedDay);
      if (fallbackIdx !== -1) {
        d = calendarStartDate ? new Date(calendarStartDate.getTime()) : new Date();
        d.setHours(0, 0, 0, 0);
        var fallbackDiff = (fallbackIdx - d.getDay() + 7) % 7;
        d.setDate(d.getDate() + fallbackDiff);
        var fallbackGuard = 0;
        while (!isCalendarDateSelectable(calendarData, d) && fallbackGuard < 100) {
          d.setDate(d.getDate() + 7);
          fallbackGuard++;
        }
        if (fallbackGuard >= 100 && !isCalendarDateSelectable(calendarData, d)) {
          d = null;
        } else {
          this.deliveryDate = getIsoFromDate(d);
        }
      }
    }

    if (d) {
      var selectedIso = getIsoFromDate(d);
      this.subscriptionDeliveryDate = selectedIso;
      if (this.purchaseType === 'subscription') {
        this.deliveryDate = selectedIso;
      } else if (this.onetimeDeliveryDate) {
        this.deliveryDate = this.onetimeDeliveryDate;
      }

      var shortFormatted = d.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric'
      });

      // Update one-time delivery text
      if (this.onetimeDelivery) {
        this.onetimeDelivery.innerHTML = 'Get your order on <b>' + shortFormatted + '</b>';
      }

      // Update subscription first order text
      if (this.subFirstOrder) {
        this.subFirstOrder.innerHTML = 'Get your first order on <b>' + shortFormatted + '</b>';
      }
    } else {
      if (this.subFirstOrder) this.subFirstOrder.textContent = '';
    }
  };

  /* -----------------------------------------------------------------------
     Checkout
     ----------------------------------------------------------------------- */
  /* -----------------------------------------------------------------------
     Step Navigation
     ----------------------------------------------------------------------- */
  CartSidebar.prototype.showWaitlist = function () {
    if (this.waitlist) this.waitlist.style.display = '';
    if (this.waitlistMsg) { this.waitlistMsg.style.display = 'none'; this.waitlistMsg.textContent = ''; }
    if (this.waitlistForm) this.waitlistForm.style.display = '';
  };

  CartSidebar.prototype.hideWaitlist = function () {
    if (this.waitlist) this.waitlist.style.display = 'none';
  };

  CartSidebar.prototype.submitWaitlist = function () {
    var email = this.waitlistEmail ? this.waitlistEmail.value.trim() : '';
    if (!email) return;

    var zip = this.zipInput ? this.zipInput.value.trim() : '';
    var btn = this.waitlist.querySelector('.cs-waitlist-btn');
    var self = this;

    if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }

    // Submit to Klaviyo via their subscribe endpoint
    var formData = new FormData();
    formData.append('g', 'VNfrcs');
    formData.append('email', email);
    formData.append('$fields', '$source');
    formData.append('$source', 'Waitlist - ' + (zip || 'unknown zip'));

    fetch('https://manage.kmail-lists.com/subscriptions/subscribe', {
      method: 'POST',
      body: formData,
      mode: 'no-cors'
    })
      .catch(function (err) { console.warn('Waitlist submit error', err); })
      .finally(function () {
        var _learnq = window._learnq || [];
        _learnq.push(['identify', { '$email': email, 'Waitlist Zip': zip, 'Waitlist Source': 'cart-sidebar' }]);
        _learnq.push(['track', 'Waitlist Signup', { zip: zip }]);

        if (self.waitlistForm) self.waitlistForm.style.display = 'none';
        if (self.waitlistMsg) {
          self.waitlistMsg.textContent = "You're on the list! We'll let you know when we deliver to your area.";
          self.waitlistMsg.className = 'cs-waitlist-msg cs-waitlist-msg--success';
          self.waitlistMsg.style.display = '';
        }
      });
  };

  CartSidebar.prototype.goToStep = function (step) {
    this.currentStep = step;

    if (step === 2) {
      // Slide to Step 2
      if (this.stepsTrack) this.stepsTrack.classList.add('cs-at-step-2');
      if (this.backLink) this.backLink.style.display = '';

      // If zip is already confirmed, show delivering row + toggle immediately
      if (this.zipConfirmedCode) {
        this.showZipConfirmed(this.zipConfirmedCode);
        this.toggleEl.style.display = '';
        if (this.purchaseType === 'subscription') {
          this.subOptions.style.display = '';
        }
        if (this.purchaseType === 'onetime' && this.onetimeCalendar) {
          if (this.calendarInitialized) {
            this.onetimeCalendar.style.display = '';
          } else {
            this._autoShowCalendar = true;
          }
        }
      } else {
        // Show zip input
        if (this.zipRow) this.zipRow.style.display = '';
        this.toggleEl.style.display = 'none';
      }
    } else {
      // Slide back to Step 1
      if (this.stepsTrack) this.stepsTrack.classList.remove('cs-at-step-2');
      if (this.backLink) this.backLink.style.display = 'none';
    }

    // Update button text/state for the current step
    this.updateCheckoutButton();

    // Dispatch event for header progress bar
    if (step === 2) {
      document.dispatchEvent(new CustomEvent('progressbar:stage-change', { detail: { stage: 3 } }));
    } else if (step === 1) {
      document.dispatchEvent(new CustomEvent('progressbar:stage-change', { detail: { stage: 2 } }));
    }
  };

  CartSidebar.prototype.updateCheckoutButton = function () {
    if (!this.cart) return;
    var btnText = this.checkoutBtn ? this.checkoutBtn.querySelector('.cs-checkout-text') : null;
    var btnLock = this.checkoutBtn ? this.checkoutBtn.querySelector('.cs-checkout-lock') : null;

    var total = this.getEffectiveCheckoutTotal();
    var min = this.isUSAEast
      ? USA_EAST_THRESHOLDS.minimumOrder
      : ((this.purchaseType === 'subscription')
        ? THRESHOLDS.subscription.minimumOrder
        : THRESHOLDS.onetime.minimumOrder);

    if (total < min) {
      // Below minimum — always disabled regardless of step
      if (this.checkoutBtn) this.checkoutBtn.disabled = true;
      if (btnText) btnText.textContent = '$' + (min / 100) + ' minimum to checkout';
      if (btnLock) btnLock.style.display = 'none';
      return;
    }

    if (btnLock) btnLock.style.display = 'flex';

    if (this.currentStep === 1) {
      // Step 1: button advances to Step 2
      if (this.checkoutBtn) this.checkoutBtn.disabled = false;
      if (btnText) btnText.textContent = 'Continue to Checkout';
    } else {
      // Step 2: three states
      var zipInputHasText = (this.zipInput && (this.zipInput.value || '').trim().length > 0);
      if (this.zipConfirmedCode) {
        // Zip confirmed — options are visible, ready to checkout
        if (this.checkoutBtn) this.checkoutBtn.disabled = false;
        if (btnText) btnText.textContent = 'Continue to Checkout';
      } else if (zipInputHasText) {
        // Something typed but not yet validated — prompt to validate
        if (this.checkoutBtn) this.checkoutBtn.disabled = false;
        if (btnText) btnText.textContent = 'Enter postal code';
      } else {
        // Nothing entered
        if (this.checkoutBtn) this.checkoutBtn.disabled = true;
        if (btnText) btnText.textContent = 'Enter postal code';
      }
    }
  };

  CartSidebar.prototype.checkout = function () {
    if (this.isCheckingOut) return; // Prevent double-click

    // Final minimum check before proceeding
    var total = (this.purchaseType === 'subscription')
      ? this.getSubscriptionTotal()
      : this.getCartTotal();
    var min = this.isUSAEast
      ? USA_EAST_THRESHOLDS.minimumOrder
      : ((this.purchaseType === 'subscription')
        ? THRESHOLDS.subscription.minimumOrder
        : THRESHOLDS.onetime.minimumOrder);
    if (!this.cart || total < min) {
      this.updateCheckoutButton();
      return;
    }

    var self = this;
    this.isCheckingOut = true;
    this.showCheckoutLoading(true);

    var steps = [];

    // Step 1: Set cart attributes
    steps.push(function () {
      return self.setCartAttributes();
    });

    // Step 2: Apply or remove selling plans based on purchase type
    if (this.purchaseType === 'subscription') {
      steps.push(function () {
        return self.applySubscription();
      });
      // Step 2b: Add bundle props
      steps.push(function () {
        return self.addBundleProps();
      });
    } else {
      // One-time: ensure any existing selling plans are removed
      steps.push(function () {
        return self.removeSubscription();
      });
      // One-time orders must also be bundled for ReCharge
      steps.push(function () {
        return self.addBundleProps();
      });
    }

    // Execute steps in sequence then redirect
    var chain = Promise.resolve();
    steps.forEach(function (step) {
      chain = chain.then(step);
    });

    chain
      .then(function () {
        // Validate bundle props before checkout (mirrors cart-v2.js L1553)
        return fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var firstItem = cart.items && cart.items[0];
            if (firstItem && firstItem.properties && firstItem.properties._rc_bundle) {
              self.redirectToCheckout();
            } else {
              // Bundle props missing — retry once
              console.warn('CartSidebar: _rc_bundle missing, retrying addBundleProps');
              return self.addBundleProps().then(function () {
                return fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
                  .then(function (r) { return r.json(); })
                  .then(function (retryCart) {
                    var retryItem = retryCart.items && retryCart.items[0];
                    if (retryItem && retryItem.properties && retryItem.properties._rc_bundle) {
                      self.redirectToCheckout();
                    } else {
                      console.error('CartSidebar: _rc_bundle still missing after retry');
                      window.location.reload();
                    }
                  });
              });
            }
          });
      })
      .catch(function (err) {
        console.error('CartSidebar: checkout error', err);
        // Re-fetch cart to get the actual state, then allow retry
        self.fetchCart(function (cart) {
          self.cart = cart;
          self.isCheckingOut = false;
          self.showCheckoutLoading(false);
          self.render();
        });
      });
  };

  CartSidebar.prototype.setCartAttributes = function () {
    var attrs = {
      'Checkout-Method': 'delivery'
    };

    if (this.zapietLocationId) {
      attrs['Delivery-Location-Id'] = String(this.zapietLocationId);
    }

    if (this.deliveryDay) {
      attrs['Delivery-Day'] = this.deliveryDay.charAt(0).toUpperCase() + this.deliveryDay.slice(1);
    }

    if (this.deliveryDate) {
      var d = new Date(this.deliveryDate + 'T00:00:00');
      var dateStr = d.getFullYear() + '/' +
        ('0' + (d.getMonth() + 1)).slice(-2) + '/' +
        ('0' + d.getDate()).slice(-2);
      attrs['Delivery-Date'] = dateStr;
    }

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) { return r.json(); });
  };

  /**
   * Fetch a product's selling plan groups via Shopify's product JSON endpoint.
   * Returns the selling_plan matching the given frequency + charge_day, or null.
   */
  CartSidebar.prototype.findSellingPlan = function (productHandle, frequency, chargeDay) {
    var TRANSLATION = {
      weekly: '1 week',
      biweekly: '2 week',
      monday: 'Monday',
      sunday: 'Sunday'
    };
    var freqStr = TRANSLATION[frequency];
    var dayStr = TRANSLATION[chargeDay];

    return fetch('/products/' + productHandle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var groups = product.selling_plan_groups || [];
        for (var g = 0; g < groups.length; g++) {
          // Filter to ReCharge app (app_id 294517)
          if (groups[g].app_id && String(groups[g].app_id) !== '294517') continue;
          var plans = groups[g].selling_plans || [];
          for (var p = 0; p < plans.length; p++) {
            if (plans[p].name.indexOf(freqStr) !== -1 && plans[p].name.indexOf(dayStr) !== -1) {
              return plans[p].id;
            }
          }
        }
        return null;
      });
  };

  /**
   * Clear the cart and verify it's actually empty before resolving.
   * Show styled clear-cart confirmation popup (replaces browser confirm())
   */
  CartSidebar.prototype.showClearCartPopup = function () {
    var self = this;
    var tmpl = document.getElementById('csClearCartTemplate');
    if (!tmpl) return;

    var existing = document.getElementById('csClearOverlay');
    if (existing) existing.remove();

    document.body.appendChild(tmpl.content.cloneNode(true));
    var overlay = document.getElementById('csClearOverlay');
    if (!overlay) return;

    var closeBtn = document.getElementById('csClearClose');
    var cancelBtn = document.getElementById('csClearCancel');
    var confirmBtn = document.getElementById('csClearConfirm');
    var errEl = document.getElementById('csClearError');

    function close() { if (overlay) overlay.remove(); }

    closeBtn.addEventListener('click', close);
    cancelBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });

    confirmBtn.addEventListener('click', function () {
      confirmBtn.disabled = true;
      confirmBtn.innerHTML = '<span class="cs-clear-spinner"></span>';
      if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }

      fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
        .then(function () {
          self.cart = { items: [], item_count: 0, total_price: 0 };
          try {
            self.renderItems();
            self.updateProgress();
            self.updateCheckoutButton();
          } catch (e) { console.warn('CartSidebar: UI update after clear', e); }
          self.collapseMobile();
          close();
          if (window.locationRouting && typeof window.locationRouting.openRegionModal === 'function') {
            // Scope the region modal to the country of the postal code the customer
            // just entered, not the current domain — a US zip on the .ca site must
            // still offer the US regions.
            window.locationRouting.openRegionModal(self.lastValidatedCountry);
          }
        })
        .catch(function (err) {
          console.error('CartSidebar: cart clear failed', err);
          confirmBtn.disabled = false;
          confirmBtn.textContent = 'Confirm';
          if (errEl) {
            errEl.textContent = "Couldn't clear your cart. Please try again.";
            errEl.style.display = '';
          }
        });
    });
  };

  /**
   * Retries up to maxRetries times if items remain after clearing.
   */
  CartSidebar.prototype.clearCartAndVerify = function (maxRetries) {
    maxRetries = maxRetries || 3;
    var attempt = 0;

    function tryClear() {
      return fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          // Verify cart is actually empty
          return fetch('/cart.js', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });
        })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart.item_count === 0) return cart;
          attempt++;
          if (attempt >= maxRetries) {
            console.warn('CartSidebar: cart not empty after ' + maxRetries + ' clear attempts, forcing');
            return cart;
          }
          return tryClear();
        });
    }

    return tryClear();
  };

  // Rebuild cart items when switching between subscription and one-time
  // so Shopify can correctly calculate discounts for the current purchase type
  CartSidebar.prototype.rebuildCartForPurchaseType = function (type) {
    var self = this;
    if (!this.cart || !this.cart.items || this.cart.items.length === 0) {
      self.renderDiscountBanner();
      self.updateProgress();
      self.updateTotals();
      return;
    }

    var hasSP = this.cart.items.some(function (item) {
      return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    });

    var needsRebuild = (type === 'subscription' && !hasSP) || (type === 'onetime' && hasSP);

    if (!needsRebuild) {
      self.renderDiscountBanner();
      self.updateProgress();
      self.updateTotals();
      return;
    }

    // Show full-cart loading overlay
    if (self.switchingOverlay) self.switchingOverlay.style.display = '';

    var rebuildPromise = (type === 'subscription')
      ? self.applySubscription()
      : self.removeSubscription();

    rebuildPromise
      .then(function () {
        return fetch('/cart.js', {
          method: 'GET', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' }
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self.cart = cart;
        self.render();
        self._selfDispatch = true;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        if (typeof syncCollectionQuantities === 'function') syncCollectionQuantities(cart);
      })
      .catch(function (err) {
        console.error('CartSidebar: rebuildCart error', err);
        self.renderDiscountBanner();
        self.updateProgress();
        self.updateTotals();
      })
      .then(function () {
        if (self.switchingOverlay) self.switchingOverlay.style.display = 'none';
      });
  };

  CartSidebar.prototype.applySubscription = function () {
    var self = this;
    var frequency = this.frequency;
    var chargeDay = (this.daySelect && this.daySelect.value) ? this.daySelect.value : this.deliveryDay;

    // Fetch current cart
    return fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // For each item, fetch its product and find the matching selling plan
        var promises = cart.items.map(function (item) {
          return self.findSellingPlan(item.handle, frequency, chargeDay)
            .then(function (sellingPlanId) {
              var entry = {
                id: item.variant_id,
                quantity: item.quantity,
                properties: item.properties || {}
              };
              if (sellingPlanId) {
                entry.selling_plan = sellingPlanId;
              }
              return entry;
            });
        });
        return Promise.all(promises);
      })
      .then(function (items) {
        // Clear cart, verify it's empty, then add subscription items
        return self.clearCartAndVerify().then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          });
        })
          .then(function (r) { return r.json(); });
      });
  };

  CartSidebar.prototype.removeSubscription = function () {
    // Concurrency guard: if already running, return the existing promise
    if (this._removeSubPromise) return this._removeSubPromise;
    var self = this;

    this._removeSubPromise = fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Check if any item actually has a selling plan
        var hasSellingPlan = cart.items.some(function (item) {
          return item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
        });
        if (!hasSellingPlan) return Promise.resolve();

        // Save items to re-add without selling plans
        var items = cart.items.map(function (item) {
          return {
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {}
          };
        });

        // Clear cart, verify it's empty, then add one-time items
        return self.clearCartAndVerify().then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          });
        })
          .then(function (r) { return r.json(); });
      })
      .then(function (result) {
        self._removeSubPromise = null;
        return result;
      }, function (err) {
        self._removeSubPromise = null;
        throw err;
      });

    return this._removeSubPromise;
  };

  CartSidebar.prototype.addBundleProps = function () {
    if (typeof window.bear === 'undefined' || typeof window.bear.addBundlePropsToItems !== 'function') {
      return Promise.resolve();
    }

    var locId = this.zapietLocationId;
    var bundleKey = 'default';

    if ([242677, 270213, 285794, 289774, 319959].indexOf(locId) !== -1) {
      bundleKey = 'toronto';
    } else if ([273216, 276649, 277960, 278438, 279269, 288292, 299051, 301750].indexOf(locId) !== -1) {
      bundleKey = 'usa-west';
    } else if ([301948, 310459, 310466].indexOf(locId) !== -1) {
      bundleKey = 'usa-east';
    }

    return window.bear.addBundlePropsToItems(bundleKey);
  };

  /* Checkout locale follows the active Weglot language — see assets/checkout-locale.js. */
  CartSidebar.prototype.getCheckoutLocale = function () {
    return window.igoCheckoutLocale ? window.igoCheckoutLocale() : 'en-AU';
  };

  CartSidebar.prototype.redirectToCheckout = function () {
    var selectedPromoConfig = this.getSelectedPromoConfig();
    var discountCode = selectedPromoConfig ? selectedPromoConfig.code : (this.getUrlDiscountCode() || this.getCookie('discount_code'));
    if (this.purchaseType === 'onetime' && !ENABLE_ONETIME_TOGGLE_DISCOUNT) {
      discountCode = '';
    }
    var checkoutUrl = '/checkout';
    var params = [];

    params.push('locale=' + this.getCheckoutLocale());
    params.push('method=delivery');
    params.push('skip_shopify_pay=true');
    params.push('step=contact_information');

    checkoutUrl += '?' + params.join('&');

    var url = checkoutUrl;
    if (discountCode) {
      url = '/discount/' + encodeURIComponent(normalizeDiscountCode(discountCode)) + '?redirect=' + encodeURIComponent(checkoutUrl);
    }

    window.location.href = url;
  };

  CartSidebar.prototype.showCheckoutLoading = function (show) {
    var textEl = this.checkoutBtn.querySelector('.cs-checkout-text');
    var spinnerEl = this.checkoutBtn.querySelector('.cs-checkout-spinner');
    textEl.style.display = show ? 'none' : '';
    spinnerEl.style.display = show ? '' : 'none';
    this.checkoutBtn.disabled = show;
  };

  CartSidebar.prototype.getCookie = function (name) {
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  };

  /* -----------------------------------------------------------------------
     Mobile Toggle
     ----------------------------------------------------------------------- */
  CartSidebar.prototype.toggleMobile = function () {
    if (this.sidebar.classList.contains('cs-expanded')) {
      this.collapseMobile();
    } else {
      this.expandMobile();
    }
  };

  CartSidebar.prototype.expandMobile = function () {
    var wrapper = this.sidebar.closest('.collection-sidebar-wrapper');
    this.sidebar.classList.add('cs-expanded');
    document.body.classList.add('cs-mobile-cart-open');
    document.documentElement.classList.add('cs-mobile-cart-open');
    if (this.mobileAction) this.mobileAction.textContent = 'Hide';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Prevent background scroll on iOS by blocking touchmove outside the cart steps
    this._lockTouchMove = function (e) {
      if (e.target.closest('.cs-step')) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', this._lockTouchMove, { passive: false });

    // Force sticky header visible while cart is expanded
    var headerSection = document.getElementById('shopify-section-header');
    var headerHeight = 0;
    if (headerSection) {
      headerSection.classList.add('shopify-section-header-sticky', 'animate');
      headerSection.classList.remove('shopify-section-header-hidden');
      headerHeight = headerSection.offsetHeight;

      requestAnimationFrame(function () {
        headerSection.classList.add('shopify-section-header-sticky', 'animate');
        headerSection.classList.remove('shopify-section-header-hidden');
      });
    }

    // Stretch wrapper from just below header to bottom of screen
    if (wrapper) {
      wrapper.style.top = headerHeight + 'px';
      wrapper.style.bottom = '0';
    }
    // Make sidebar fill the wrapper
    this.sidebar.style.height = '100%';
    this.sidebar.style.display = 'flex';
    this.sidebar.style.flexDirection = 'column';

    document.dispatchEvent(new CustomEvent('cs:mobile-toggle', { detail: { expanded: true } }));
  };

  CartSidebar.prototype.collapseMobile = function () {
    var wrapper = this.sidebar.closest('.collection-sidebar-wrapper');
    this.sidebar.classList.remove('cs-expanded');
    document.body.classList.remove('cs-mobile-cart-open');
    document.documentElement.classList.remove('cs-mobile-cart-open');
    if (this.mobileAction) this.mobileAction.textContent = 'View Cart';

    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    if (this._lockTouchMove) {
      document.removeEventListener('touchmove', this._lockTouchMove);
      this._lockTouchMove = null;
    }

    // Reset wrapper and sidebar styles
    if (wrapper) {
      wrapper.style.top = '';
      wrapper.style.bottom = '';
    }
    this.sidebar.style.height = '';
    this.sidebar.style.display = '';
    this.sidebar.style.flexDirection = '';
    if (this.content) this.content.style.maxHeight = '';

    document.dispatchEvent(new CustomEvent('cs:mobile-toggle', { detail: { expanded: false } }));
  };

  CartSidebar.prototype.updateMobileBar = function () {
    if (!this.mobileCount || !this.mobileSubtotal || !this.cart) return;
    var count = this.cart.item_count || 0;
    var total;
    var selectedPromoConfig = this.getSelectedPromoConfig();
    if (selectedPromoConfig) {
      total = this.getPromoDisplayTotals(selectedPromoConfig).total / 100;
    } else {
      total = (this.purchaseType === 'subscription' ? this.getCartTotal() : this.getOriginalTotal()) / 100;
    }

    this.mobileCount.textContent = count;
    this.mobileSubtotal.textContent = '$' + total.toFixed(2);

    // Update mobile progress
    if (this.mobileProgress) {
      var totalCents = this.getEffectiveCheckoutTotal();

      var mbThresholds = (this.purchaseType === 'subscription')
        ? THRESHOLDS.subscription
        : THRESHOLDS.onetime;
      var mbMin = this.isUSAEast ? USA_EAST_THRESHOLDS.minimumOrder : mbThresholds.minimumOrder;
      var mbFree = this.isUSAEast ? USA_EAST_THRESHOLDS.freeDelivery : mbThresholds.freeDelivery;
      var mbHasFree = this.isUSAEast || !this.isUS;

      if (this.isUSAEast && this.mobileDotMin) {
        this.updateMobileMilestone(totalCents, mbMin, mbFree);
      } else {
        // Simple bar for non-USA East regions
        if (this.mobileProgressBar) this.mobileProgressBar.classList.remove('cs-milestone-mode');
        if (this.mobileDotMin) this.mobileDotMin.style.display = 'none';
        if (this.mobileDotFree) this.mobileDotFree.style.display = 'none';
        if (this.mobileLabels) this.mobileLabels.style.display = 'none';

        var mobilePercentage = Math.min((totalCents / mbMin) * 100, 100);

        if (totalCents < mbMin) {
          var needed = Math.ceil((mbMin - totalCents) / 100);
          this.mobileProgress.innerHTML = "\u24D8 You're <strong>$" + needed + "</strong> away from checkout";
          this.mobileProgress.classList.remove('cs-progress-met');
        } else if (mbHasFree && totalCents < mbFree) {
          var mbAway = ((mbFree - totalCents) / 100).toFixed(2);
          this.mobileProgress.innerHTML = "$" + mbAway + " away from <strong>free delivery</strong>";
          this.mobileProgress.classList.add('cs-progress-met');
        } else {
          mobilePercentage = 100;
          this.mobileProgress.innerHTML = mbHasFree
            ? "\u2713 Free delivery unlocked!"
            : "\u2713 You're all set to checkout";
          this.mobileProgress.classList.add('cs-progress-met');
        }

        if (this.mobileProgressFill) {
          this.mobileProgressFill.style.width = mobilePercentage + '%';
        }
      }
    }
  };

  CartSidebar.prototype.updateMobileMilestone = function (total, min, free) {
    var barMax = USA_EAST_THRESHOLDS.barMax;
    var minMet = total >= min;
    var freeMet = total >= free;

    var minPct = (min / barMax) * 100;
    var freePct = (free / barMax) * 100;
    var fillPct = Math.min((total / barMax) * 100, 100);

    this.mobileProgressBar.classList.add('cs-milestone-mode');
    this.mobileDotMin.style.display = '';
    this.mobileDotFree.style.display = '';
    if (this.mobileLabels) this.mobileLabels.style.display = '';

    // Fill bar
    if (this.mobileProgressFill) {
      this.mobileProgressFill.style.width = fillPct + '%';
      this.mobileProgressFill.className = 'cs-progress-fill' +
        (!minMet ? ' cs-fill--warning' : (!freeMet ? ' cs-fill--progress' : ' cs-fill--success'));
    }

    // Min dot
    this.mobileDotMin.style.left = minPct + '%';
    this.mobileDotMin.className = 'cs-mobile-dot' +
      (minMet ? ' cs-mobile-dot--met' : ' cs-mobile-dot--unmet');

    // Free delivery dot
    this.mobileDotFree.style.left = freePct + '%';
    this.mobileDotFree.className = 'cs-mobile-dot' +
      (freeMet ? ' cs-mobile-dot--met' : ' cs-mobile-dot--unmet-muted');

    // Labels
    if (this.mobileLabelMin) {
      this.mobileLabelMin.style.left = minPct + '%';
      this.mobileLabelMin.textContent = '$' + (min / 100) + ' min';
      this.mobileLabelMin.className = 'cs-mobile-milestone-label' +
        (minMet ? ' cs-mobile-milestone-label--met' : ' cs-mobile-milestone-label--unmet');
    }
    if (this.mobileLabelFree) {
      this.mobileLabelFree.style.left = freePct + '%';
      this.mobileLabelFree.textContent = 'Free Delivery';
      this.mobileLabelFree.className = 'cs-mobile-milestone-label' +
        (freeMet ? ' cs-mobile-milestone-label--met' : ' cs-mobile-milestone-label--muted');
    }

    // Text
    if (!minMet) {
      var needed = Math.ceil((min - total) / 100);
      this.mobileProgress.innerHTML = "ⓘ You're <strong>$" + needed + "</strong> away from checkout";
      this.mobileProgress.classList.remove('cs-progress-met');
    } else if (!freeMet) {
      var away = ((free - total) / 100).toFixed(2);
      this.mobileProgress.innerHTML = "$" + away + " away from <strong>free delivery</strong>";
      this.mobileProgress.classList.add('cs-progress-met');
    } else {
      this.mobileProgress.innerHTML = "✓ Free delivery unlocked!";
      this.mobileProgress.classList.add('cs-progress-met');
    }
  };

  /* -----------------------------------------------------------------------
     Initialize on DOM ready
     ----------------------------------------------------------------------- */
  function initSidebar() {
    // Only init on collection pages
    if (window.location.pathname.indexOf('/collections/') === -1) return;
    if (!document.getElementById('cartSidebar')) return;
    window.__cartSidebar = new CartSidebar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSidebar);
  } else {
    initSidebar();
  }
})();
